import { useState, useEffect, useRef } from 'react'
import { useQuery, useMutation, useQueryClient } from 'react-query'
import { useCatalogSettings } from '../contexts/CatalogContext'
import { getImageUrl } from '../utils/apiUrl'
import { 
  Settings, 
  Save, 
  RefreshCw, 
  CheckCircle, 
  XCircle, 
  AlertCircle,
  Square,
  ExternalLink,
  Database,
  Download,
  Upload,
  Package,
  Globe,
  Shield,
  Search,
  Calendar,
  Tag,
  Folder,
  User,
  Users,
  Mail,
  Briefcase,
  Activity,
  Trash2,
  Table,
  Key,
  Hash,
  ChevronDown,
  ChevronRight,
  FileText,
  Plus,
  Edit,
  Image,
  GripVertical,
  Layers
} from 'lucide-react'
import { LayoutGrid, ChevronUp } from 'lucide-react'
import apiService from '../services/api'
import toast from 'react-hot-toast'
import { Link, useNavigate, useSearchParams } from 'react-router-dom'

const AdminSettings = () => {
  const { settings: catalogSettings, updateSettings: updateCatalogSettings } = useCatalogSettings()
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const [activeTab, setActiveTab] = useState('general')
  const [activeRentmanTab, setActiveRentmanTab] = useState('import')
  const [activeGeneralTab, setActiveGeneralTab] = useState('company')
  const [expandedMenus, setExpandedMenus] = useState({})
  const [isOptimizing, setIsOptimizing] = useState(false)
  const [optimizationLogs, setOptimizationLogs] = useState([])
  const [optimizationProgress, setOptimizationProgress] = useState(0)
  const [optimizationTimeout, setOptimizationTimeout] = useState(null)
  
  // Paramètres d'optimisation
  const [webpQuality, setWebpQuality] = useState(() => {
    const saved = localStorage.getItem('optimization_webp_quality')
    return saved ? parseInt(saved) : 85
  })
  const [enableResize, setEnableResize] = useState(() => {
    const saved = localStorage.getItem('optimization_enable_resize')
    return saved ? JSON.parse(saved) : true
  })
  const [maxWidth, setMaxWidth] = useState(() => {
    const saved = localStorage.getItem('optimization_max_width')
    return saved ? parseInt(saved) : 1920
  })
  const [maxHeight, setMaxHeight] = useState(() => {
    const saved = localStorage.getItem('optimization_max_height')
    return saved ? parseInt(saved) : 1080
  })
  const [jpegQuality, setJpegQuality] = useState(() => {
    const saved = localStorage.getItem('optimization_jpeg_quality')
    return saved ? parseInt(saved) : 80
  })
  const [isSavingSettings, setIsSavingSettings] = useState(false)
  const [settingsSaved, setSettingsSaved] = useState(false)
  const [pages, setPages] = useState([])
  
  // États pour le formulaire de page
  const [pageFormData, setPageFormData] = useState({
    title: '',
    slug: '',
    content: '',
    meta_description: '',
    is_published: false
  })
  
  // États pour le poids en temps réel
  const [originalWeight, setOriginalWeight] = useState(0)
  const [optimizedWeight, setOptimizedWeight] = useState(0)
  const [folderWeights, setFolderWeights] = useState(null)
  const [isLoadingWeights, setIsLoadingWeights] = useState(false)
  
  // Fonction pour charger les poids des dossiers
  const loadFolderWeights = async () => {
    setIsLoadingWeights(true)
    try {
      const response = await apiService.get('/images/folder-weights')
      if (response.data?.success) {
        setFolderWeights(response.data.data)
        setOriginalWeight(response.data.data.original.weight)
        setOptimizedWeight(response.data.data.optimized.weight)
        console.log('📊 Poids des dossiers chargés:', response.data.data)
      }
    } catch (error) {
      console.error('❌ Erreur chargement poids dossiers:', error.message)
    } finally {
      setIsLoadingWeights(false)
    }
  }

  // Gérer les paramètres URL pour ouvrir directement l'onglet pages et éditer une page
  useEffect(() => {
    const tabParam = searchParams.get('tab')
    const editParam = searchParams.get('edit')
    
    if (tabParam === 'pages') {
      setActiveTab('general')
      setActiveGeneralTab('pages')
      
      // Si on doit éditer une page spécifique, la charger
      if (editParam) {
        // Attendre que les pages soient chargées
        setTimeout(() => {
          const pageToEdit = pages.find(p => p.slug === editParam)
          if (pageToEdit) {
            setEditingPage(pageToEdit)
            setPageFormData({
              title: pageToEdit.title || '',
              slug: pageToEdit.slug || '',
              content: pageToEdit.content || '',
              meta_description: pageToEdit.meta_description || '',
              is_published: pageToEdit.is_published || false
            })
            setShowPageForm(true)
          } else {
            // Page n'existe pas encore, créer avec le slug
            setEditingPage({ slug: editParam })
            setPageFormData({
              title: '',
              slug: editParam,
              content: '',
              meta_description: '',
              is_published: false
            })
            setShowPageForm(true)
          }
        }, 500)
      }
    }
  }, [searchParams, pages])

  // Charger les paramètres sauvegardés au démarrage
  useEffect(() => {
    const hasSettings = localStorage.getItem('optimization_webp_quality')
    if (hasSettings) {
      setOptimizationLogs([{
        type: 'info',
        message: '📁 Paramètres d\'optimisation chargés depuis la sauvegarde précédente.',
        timestamp: new Date().toLocaleTimeString()
      }])
    }
    
    // Charger le nombre d'images depuis la base de données
    const loadImageCount = async () => {
      try {
        const response = await apiService.get('/images/catalog/images')
        const imageCount = response.data?.length || 0
        const countElement = document.getElementById('image-count')
        if (countElement) {
          countElement.textContent = imageCount.toLocaleString()
        }
      } catch (error) {
        console.log('API /images/catalog/images non disponible, utilisation du nombre par défaut')
        const countElement = document.getElementById('image-count')
        if (countElement) {
          countElement.textContent = '3' // Nombre par défaut réaliste
        }
      }
    }
    
    loadImageCount()
    
    // Charger les poids des dossiers
    loadFolderWeights()
  }, [])
  const [editingPage, setEditingPage] = useState(null)
  const [showPageForm, setShowPageForm] = useState(false)
  
  // Charger les pages depuis l'API
  const { data: pagesData, refetch: refetchPages } = useQuery(
    'admin-pages',
    () => apiService.get('/pages').then(res => res.data),
    {
      enabled: activeGeneralTab === 'pages',
      onSuccess: (data) => {
        if (data?.pages) {
          setPages(data.pages)
        }
      }
    }
  )
  
  // Mutation pour créer/mettre à jour une page
  const savePageMutation = useMutation(
    async (pageData) => {
      if (editingPage?.id) {
        return apiService.put(`/pages/${editingPage.id}`, pageData)
      } else {
        return apiService.post('/pages', pageData)
      }
    },
    {
      onSuccess: () => {
        toast.success(editingPage ? 'Page mise à jour avec succès' : 'Page créée avec succès')
        refetchPages()
        setShowPageForm(false)
        setEditingPage(null)
        setPageFormData({
          title: '',
          slug: '',
          content: '',
          meta_description: '',
          is_published: false
        })
      },
      onError: (error) => {
        toast.error(error?.response?.data?.error || 'Erreur lors de la sauvegarde')
      }
    }
  )
  const [expandedTables, setExpandedTables] = useState({})
  const [adminInfo, setAdminInfo] = useState(null)
  const [isUserMenuOpen, setIsUserMenuOpen] = useState(false)
  const [deletingProjectId, setDeletingProjectId] = useState(null)
  const [isSaving, setIsSaving] = useState(false)
  const userMenuRef = useRef(null)
  
  // États pour la gestion des projets Rentman
  const [projectId, setProjectId] = useState('')
  
  // Fonction pour supprimer un projet exporté
  const handleDeleteProject = async (projectId) => {
    if (!window.confirm(`Êtes-vous sûr de vouloir supprimer le projet ${projectId} de Rentman ?`)) {
      return
    }
    
    setDeletingProjectId(projectId)
    try {
      await apiService.delete(`/rentman/projectrequests/${projectId}`)
      toast.success(`Projet ${projectId} supprimé avec succès`)
    } catch (error) {
      console.error('Erreur suppression projet:', error)
      toast.error(error.response?.data?.message || 'Erreur lors de la suppression du projet')
    } finally {
      setDeletingProjectId(null)
    }
  }
  const [projectData, setProjectData] = useState(null)
  const [isLoadingProject, setIsLoadingProject] = useState(false)
  const [projectError, setProjectError] = useState('')
  const [searchType, setSearchType] = useState('number') // 'id' ou 'number'
  
  
  
  // États pour la gestion des utilisateurs
  const [users, setUsers] = useState([])
  const [editingUser, setEditingUser] = useState(null)
  const [showUserForm, setShowUserForm] = useState(false)
  const [isSavingUser, setIsSavingUser] = useState(false)
  const [isLoadingUsers, setIsLoadingUsers] = useState(false)
  const [userRoleFilter, setUserRoleFilter] = useState('admin')
  const [userSearchTerm, setUserSearchTerm] = useState('')

  // Réinitialiser les sous-onglets quand on change d'onglet principal
  const handleTabChange = (tabId) => {
    setActiveTab(tabId)
    if (tabId === 'rentman') {
      setActiveRentmanTab('import')
    } else if (tabId === 'general') {
      setActiveGeneralTab('company')
    }
  }

  // Gérer les menus déroulants
  const toggleMenu = (menuId) => {
    setExpandedMenus(prev => ({
      ...prev,
      [menuId]: !prev[menuId]
    }))
  }

  // Lancer l'optimisation des images
  const startOptimization = async () => {
    console.log('🚀 Démarrage de l\'optimisation...')
    setIsOptimizing(true)
    setOptimizationLogs([])
    setOptimizationProgress(0)
    
    // Variable locale pour contrôler l'optimisation
    let isRunning = true
    
    // Stocker la fonction d'arrêt dès le début
    window.stopOptimizationRef = () => {
      console.log('⏹️ Arrêt de l\'optimisation demandé')
      isRunning = false
      setIsOptimizing(false)
      setOptimizationLogs(prev => [...prev, {
        type: 'error',
        message: `⏹️ Optimisation arrêtée par l'utilisateur.`,
        timestamp: new Date().toLocaleTimeString()
      }])
    }
    
    try {
      console.log('📡 Récupération des images depuis l\'API...')
      // Récupérer les images depuis l'API
      const response = await apiService.get('/images/catalog/images')
      const images = response.data || []
      const imageCount = images.length
      console.log(`📊 ${imageCount} images trouvées:`, images)
      
      // Calculer le poids total original
      const totalOriginalWeight = images.reduce((sum, img) => sum + (img.originalSize / 1024 / 1024), 0)
      setOriginalWeight(totalOriginalWeight)
      setOptimizedWeight(0) // Reset du poids optimisé
      
      // Logs de démarrage
      setOptimizationLogs([
        {
          type: 'info',
          message: `🚀 Démarrage de l'optimisation des images... (${images.length} images détectées)`,
          timestamp: new Date().toLocaleTimeString()
        },
        {
          type: 'info',
          message: `⚙️ Paramètres: WebP ${webpQuality}%, Redimensionnement ${enableResize ? 'activé' : 'désactivé'}${enableResize ? ` (${maxWidth}x${maxHeight})` : ''}`,
          timestamp: new Date().toLocaleTimeString()
        }
      ])
      
      // Lancer l'optimisation réelle
      console.log('🔄 Lancement de l\'optimisation avec les paramètres:', { webpQuality, enableResize, maxWidth, maxHeight })
      const optimizationResponse = await apiService.post('/images/optimize-all', {
        webpQuality,
        enableResize,
        maxWidth,
        maxHeight
      })
      console.log('✅ Réponse de l\'optimisation:', optimizationResponse.data)
      
      const results = optimizationResponse.data.results || []
      let processedCount = 0
      let totalSavings = 0
      
      console.log(`📋 ${results.length} résultats à traiter:`, results)
      
      // Traiter les résultats en temps réel
      console.log(`🔄 Début du traitement de ${results.length} résultats`)
      
      for (let i = 0; i < results.length; i++) {
        console.log(`🔄 Traitement de l'image ${i + 1}/${results.length}`)
        
        if (!isRunning) {
          console.log('⏹️ Optimisation arrêtée, sortie de la boucle')
          break
        }
        
        const result = results[i]
        console.log(`📋 Résultat ${i + 1}:`, result)
        
        const progress = Math.round(((i + 1) / results.length) * 100)
        setOptimizationProgress(progress)
        console.log(`📊 Progression: ${progress}%`)
        
        if (result.success) {
          console.log(`✅ Image ${i + 1} réussie, ajout des logs`)
          
          // Ajouter le log de traitement
          setOptimizationLogs(prev => [...prev, {
            type: 'info',
            message: `🔄 Conversion de "${result.productName}" - ${(result.originalSize / 1024 / 1024).toFixed(1)} MB → WebP`,
            timestamp: new Date().toLocaleTimeString()
          }])
          
          // Simuler un délai de traitement
          console.log(`⏳ Attente de 1.5s pour l'image ${i + 1}`)
          await new Promise(resolve => setTimeout(resolve, 1500))
          
          // Mettre à jour le poids optimisé
          const newWeight = (result.optimizedSize / 1024 / 1024)
          setOptimizedWeight(prev => {
            const newTotal = prev + newWeight
            console.log(`📦 Poids optimisé mis à jour: ${prev.toFixed(1)} MB → ${newTotal.toFixed(1)} MB`)
            return newTotal
          })
          totalSavings += result.savings || 0
          
          // Log de succès
          setOptimizationLogs(prev => [...prev, {
            type: 'success',
            message: `✅ "${result.productName}" converti avec succès - ${(result.originalSize / 1024 / 1024).toFixed(1)} MB → ${(result.optimizedSize / 1024 / 1024).toFixed(1)} MB (WebP ${webpQuality}%, économie: ${result.savings}%)`,
            timestamp: new Date().toLocaleTimeString()
          }])
          
          console.log(`✅ Image ${i + 1} traitée avec succès`)
        } else {
          console.log(`❌ Image ${i + 1} échouée:`, result.error)
          
          // Log d'erreur
          setOptimizationLogs(prev => [...prev, {
            type: 'error',
            message: `❌ Erreur lors de la conversion de "${result.productName}": ${result.error}`,
            timestamp: new Date().toLocaleTimeString()
          }])
        }
        
        processedCount++
        console.log(`📊 Compteur traité: ${processedCount}/${results.length}`)
      }
      
      console.log(`🏁 Boucle terminée. Processed: ${processedCount}, Total: ${results.length}`)
      
      // Message de fin
      setOptimizationLogs(prev => [...prev, {
        type: 'success',
        message: `✅ Optimisation terminée ! ${processedCount}/${results.length} images traitées. Économie totale: ${optimizationResponse.data.summary?.overallSavings || 0}%`,
        timestamp: new Date().toLocaleTimeString()
      }])
      
      isRunning = false
      setIsOptimizing(false)
      
      // Recharger les poids des dossiers après optimisation
      loadFolderWeights()
      
    } catch (error) {
      console.error('Erreur lors de la récupération des images:', error)
      setIsOptimizing(false)
      setOptimizationLogs([{
        type: 'error',
        message: '❌ Erreur lors de la récupération des images depuis la base de données',
        timestamp: new Date().toLocaleTimeString()
      }])
    }
  }

  // Arrêter l'optimisation
  const stopOptimization = () => {
    if (window.stopOptimizationRef) {
      window.stopOptimizationRef()
    }
  }

  // Sauvegarder les paramètres d'optimisation
  const saveOptimizationSettings = async () => {
    setIsSavingSettings(true)
    setSettingsSaved(false)
    
    try {
      const settings = {
        webpQuality,
        enableResize,
        maxWidth,
        maxHeight,
        jpegQuality
      }

      // Sauvegarder dans le localStorage
      localStorage.setItem('optimization_webp_quality', webpQuality.toString())
      localStorage.setItem('optimization_enable_resize', enableResize.toString())
      localStorage.setItem('optimization_max_width', maxWidth.toString())
      localStorage.setItem('optimization_max_height', maxHeight.toString())
      localStorage.setItem('optimization_jpeg_quality', jpegQuality.toString())
      
      console.log('Paramètres d\'optimisation sauvegardés:', settings)
      
      // Simuler un délai de sauvegarde
      await new Promise(resolve => setTimeout(resolve, 1000))
      
      // Afficher un message de succès
      setOptimizationLogs(prev => [...prev, {
        type: 'success',
        message: '💾 Paramètres d\'optimisation sauvegardés avec succès ! Ils seront conservés après redémarrage.',
        timestamp: new Date().toLocaleTimeString()
      }])
      
      setSettingsSaved(true)
      
      // Réinitialiser l'état après 3 secondes
      setTimeout(() => {
        setSettingsSaved(false)
      }, 3000)
      
    } catch (error) {
      console.error('Erreur lors de la sauvegarde:', error)
      setOptimizationLogs(prev => [...prev, {
        type: 'error',
        message: '❌ Erreur lors de la sauvegarde des paramètres',
        timestamp: new Date().toLocaleTimeString()
      }])
    } finally {
      setIsSavingSettings(false)
    }
  }

  // Fonctions utilitaires pour la base de données
  const toggleTable = (tableName) => {
    setExpandedTables(prev => ({
      ...prev,
      [tableName]: !prev[tableName]
    }))
  }

  const getDataTypeDisplay = (column) => {
    let type = column.data_type
    if (column.character_maximum_length) {
      type += `(${column.character_maximum_length})`
    } else if (column.numeric_precision) {
      type += `(${column.numeric_precision}`
      if (column.numeric_scale) {
        type += `,${column.numeric_scale}`
      }
      type += ')'
    }
    return type
  }
  const [isAuthenticated, setIsAuthenticated] = useState(true) // par défaut true, on invalide si 401 avéré
  
  // Fonction de sauvegarde des paramètres
  const handleSaveSettings = async () => {
    setIsSaving(true)
    try {
      await apiService.post('/rentman/settings', {
        maintenance_mode: settings.maintenance_mode,
        maintenance_message: settings.maintenance_message,
        maintenance_banner_image: settings.maintenance_banner_image
      })
      toast.success('Paramètres de maintenance sauvegardés avec succès')
    } catch (error) {
      console.error('Erreur sauvegarde paramètres:', error)
      toast.error('Erreur lors de la sauvegarde des paramètres')
    } finally {
      setIsSaving(false)
    }
  }


  // Fonctions pour la gestion des utilisateurs
  const fetchUsers = async () => {
    setIsLoadingUsers(true)
    try {
      const response = await apiService.get('/admin/users')
      setUsers(response.data)
    } catch (error) {
      console.error('Erreur chargement utilisateurs:', error)
      toast.error('Erreur lors du chargement des utilisateurs')
    } finally {
      setIsLoadingUsers(false)
    }
  }

  const handleSaveUser = async (userData) => {
    setIsSavingUser(true)
    try {
      if (editingUser) {
        await apiService.put(`/admin/users/${editingUser.id}`, userData)
        toast.success('Utilisateur modifié avec succès')
      } else {
        await apiService.post('/admin/users', userData)
        toast.success('Utilisateur créé avec succès')
      }
      setShowUserForm(false)
      setEditingUser(null)
      await fetchUsers()
    } catch (error) {
      console.error('Erreur sauvegarde utilisateur:', error)
      toast.error('Erreur lors de la sauvegarde de l\'utilisateur')
    } finally {
      setIsSavingUser(false)
    }
  }

  const handleDeleteUser = async (userId) => {
    if (!window.confirm('Êtes-vous sûr de vouloir supprimer cet utilisateur ?')) {
      return
    }
    
    try {
      await apiService.delete(`/admin/users/${userId}`)
      toast.success('Utilisateur supprimé avec succès')
      await fetchUsers()
    } catch (error) {
      console.error('Erreur suppression utilisateur:', error)
      toast.error('Erreur lors de la suppression de l\'utilisateur')
    }
  }


  // Fonction pour récupérer les données d'un projet Rentman
  const fetchRentmanProject = async (id) => {
    if (!id || id.trim() === '') {
      setProjectError('Veuillez entrer un ID de projet')
      return
    }

    if (!isAuthenticated) {
      setProjectError('Vous devez être connecté en tant qu\'administrateur')
      toast.error('Authentification requise')
      return
    }

    setIsLoadingProject(true)
    setProjectError('')
    setProjectData(null)

    try {
      const response = await apiService.get(`/rentman/projects/${id}?type=${searchType}`)
      console.log('📋 Données du projet reçues:', response.data)
      console.log('🔍 Structure du projet:', JSON.stringify(response.data.project, null, 2))
      console.log('🔍 Tous les champs disponibles:', Object.keys(response.data.project || {}))
      console.log('🔍 Valeurs exactes des prix:', {
        project_rental_price: response.data.project?.project_rental_price,
        project_sale_price: response.data.project?.project_sale_price,
        project_crew_price: response.data.project?.project_crew_price,
        project_transport_price: response.data.project?.project_transport_price,
        project_other_price: response.data.project?.project_other_price,
        project_insurance_price: response.data.project?.project_insurance_price,
        project_total_price: response.data.project?.project_total_price,
        price: response.data.project?.price,
        price_invat: response.data.project?.price_invat,
        vat_amount: response.data.project?.vat_amount
      })
      console.log('💰 Prix de location:', response.data.project?.project_rental_price)
      console.log('💸 Prix de vente:', response.data.project?.project_sale_price)
      console.log('👥 Prix équipe:', response.data.project?.project_crew_price)
      console.log('🚚 Prix transport:', response.data.project?.project_transport_price)
      console.log('📦 Autres prix:', response.data.project?.project_other_price)
      console.log('🛡️ Prix assurance:', response.data.project?.project_insurance_price)
      console.log('💰 Prix total projet:', response.data.project?.project_total_price)
      console.log('💳 Déjà facturé:', response.data.project?.already_invoiced)
      console.log('🛒 Coûts d\'achat:', response.data.project?.purchasecosts)
      console.log('💵 Prix total:', response.data.project?.price)
      console.log('💵 Prix HT:', response.data.project?.price_invat)
      console.log('📊 Montant TVA:', response.data.project?.vat_amount)
      setProjectData(response.data)
      toast.success('Projet récupéré avec succès')
    } catch (error) {
      console.error('Erreur récupération projet:', error)
      const errorMessage = error.response?.data?.error || 'Erreur lors de la récupération du projet'
      setProjectError(errorMessage)
      toast.error(errorMessage)
    } finally {
      setIsLoadingProject(false)
    }
  }

  
  // Vérifier l'authentification au chargement (token unifié + rôle admin)
  useEffect(() => {
    const token = localStorage.getItem('user_token') || localStorage.getItem('admin_token')
    if (!token) {
      setIsAuthenticated(false)
      return
    }
    apiService.get('/auth/me')
      .then((res) => {
        const user = res.data?.user || res.data?.admin || null
        const isAdmin = user?.role === 'admin'
        setIsAuthenticated(Boolean(isAdmin))
        setAdminInfo(isAdmin ? user : null)
        
      })
      .catch((err) => {
        console.error('Erreur vérification auth:', err)
        setIsAuthenticated(false)
      })
  }, [])

  // Charger les utilisateurs quand l'onglet est sélectionné
  useEffect(() => {
    if (activeGeneralTab === 'users' && isAuthenticated) {
      fetchUsers()
    }
  }, [activeGeneralTab, isAuthenticated])

  useEffect(() => {
    const onClick = (e) => {
      if (userMenuRef.current && !userMenuRef.current.contains(e.target)) {
        setIsUserMenuOpen(false)
      }
    }
    document.addEventListener('click', onClick)
    return () => document.removeEventListener('click', onClick)
  }, [])

  const handleAdminLogout = () => {
    localStorage.removeItem('admin_token')
    setIsAuthenticated(false)
    setAdminInfo(null)
    navigate('/admin/login')
  }
  
  const [settings, setSettings] = useState({
    wordpress_url: '',
    show_regular_price: true,
    show_availability: true,
    block_checkout_unavailable: true,
    allow_overrenting: false,
    datepicker_type: 'without_time',
    show_rental_period: true,
    price_display_suffix: '',
    import_status: 'published',
    import_images: true,
    import_files: true,
    import_tags: true,
    import_accessories: true,
    import_alternatives: true,
    import_folders_as_categories: true,
    import_from_folders: 'all',
    selected_folders: [],
    import_include_subfolders: true,
    auto_sync_enabled: true,
    auto_sync_daily: true,
    auto_sync_frequent: true,
    auto_sync_24h: false,
    auto_export_to_rentman: true, // Envoi automatique vers Rentman
    // Rentman API defaults
    webshop_token: '',
    rentman_account: '',
    // SMTP defaults
    smtp_host: '',
    smtp_port: 587,
    smtp_user: '',
    smtp_pass: '',
    smtp_reset_exp_minutes: 5,
    // Contact form defaults
    contact_email_destination: '',
    contact_email_subject: '[Contact FlightCase] Nouveau message de contact',
    // Maintenance defaults
    maintenance_mode: false,
    maintenance_message: '',
    maintenance_banner_image: ''
  })

  const queryClient = useQueryClient()

  // Récupérer les paramètres
  const { data: settingsData, isLoading } = useQuery(
    'rentman-settings',
    () => apiService.get('/rentman/settings').then(res => res.data),
    {
      enabled: isAuthenticated,
      onSuccess: (data) => {
        if (data.settings) {
          setSettings(prev => ({ ...prev, ...data.settings }))
          // Synchroniser les réglages catalogue locaux avec le backend si présents
          try {
            const sync = {}
            if (typeof data.settings.show_product_description !== 'undefined') sync.show_product_description = !!data.settings.show_product_description
            if (typeof data.settings.show_product_price !== 'undefined') sync.show_product_price = !!data.settings.show_product_price
            if (typeof data.settings.show_product_availability !== 'undefined') sync.show_product_availability = !!data.settings.show_product_availability
            if (typeof data.settings.show_availability_filter !== 'undefined') sync.show_availability_filter = !!data.settings.show_availability_filter
            if (Object.keys(sync).length) updateCatalogSettings(sync)
          } catch {}
        }
      },
      onError: (err) => {
        if (err?.response?.status === 401) {
          setIsAuthenticated(false)
        }
      }
    }
  )

  // Récupérer la hiérarchie des dossiers pour la sélection
  const { data: foldersData } = useQuery(
    'rentman-folders-hierarchy',
    () => apiService.get('/rentman/folders/flat').then(res => res.data),
    {
      enabled: activeTab === 'rentman' && activeRentmanTab === 'import'
    }
  )

  // Récupérer les statistiques de la base de données
  const { data: dbStats, isLoading: dbStatsLoading } = useQuery(
    'database-stats',
    () => apiService.get('/rentman/database-stats').then(res => res.data),
    {
      enabled: (activeTab === 'general' && activeGeneralTab === 'database') || activeTab === 'catalog-images'
    }
  )

  // Récupérer la structure des tables
  const { data: tableStructure, isLoading: structureLoading } = useQuery(
    'table-structure',
    () => apiService.get('/rentman/table-structure').then(res => res.data),
    {
      enabled: activeTab === 'general' && activeGeneralTab === 'database'
    }
  )

  // Données Société (placer les hooks avant tout return conditionnel)
  const { data: companyData } = useQuery(
    'company-info',
    () => apiService.get('/rentman/company').then(res => res.data),
    { enabled: activeTab === 'general' && activeGeneralTab === 'company' }
  )

  const [company, setCompany] = useState({
    name: '', address: '', phone_landline: '', phone_mobile: '', email: '',
    currency: 'EUR', currency_position: 'right', thousands_separator: ' ', decimal_separator: ',', decimal_places: 2,
    logo_path: ''
  })
  
  // États pour l'upload de logo
  const [logoFile, setLogoFile] = useState(null)
  const [isUploadingLogo, setIsUploadingLogo] = useState(false)
  useEffect(() => {
    if (companyData?.company) setCompany(companyData.company)
  }, [companyData])
  const saveCompanyMutation = useMutation(
    async () => {
      let updatedCompany = { ...company }
      
      // Si un nouveau logo est sélectionné, l'uploader d'abord
      if (logoFile) {
        setIsUploadingLogo(true)
        try {
          // Convertir le fichier en base64
          const base64Data = await new Promise((resolve, reject) => {
            const reader = new FileReader()
            reader.onload = (e) => resolve(e.target.result)
            reader.onerror = reject
            reader.readAsDataURL(logoFile)
          })
          
          const response = await apiService.post('/upload/image', {
            image: base64Data,
            filename: logoFile.name,
            folder: 'logo'
          })
          
          // Mettre à jour le chemin du logo
          updatedCompany.logo_path = response.data.path
          setLogoFile(null) // Réinitialiser le fichier sélectionné
          toast.success('Logo uploadé avec succès')
        } catch (error) {
          console.error('Erreur upload logo:', error)
          toast.error('Erreur lors de l\'upload du logo')
          throw error
        } finally {
          setIsUploadingLogo(false)
        }
      }
      
      // Sauvegarder les informations société
      return apiService.post('/rentman/company', updatedCompany)
    },
    {
      onSuccess: () => {
        toast.success('Informations société enregistrées')
        // Rafraîchir les données
        queryClient.invalidateQueries('company-info')
        queryClient.invalidateQueries('company-public')
      },
      onError: (e) => toast.error(e.response?.data?.error || 'Erreur enregistrement société')
    }
  )
  const handleCompanyChange = (field, value) => setCompany(prev => ({ ...prev, [field]: value }))
  
  // Fonction pour préparer l'upload du logo (sans upload immédiat)
  const handleLogoFileSelect = (file) => {
    setLogoFile(file)
    toast.info('Logo sélectionné. Cliquez sur "Enregistrer" pour l\'uploader.')
  }

  // Construction de l'arbre des dossiers (par parent)
  const foldersList = Array.isArray(foldersData?.folders) ? foldersData.folders : []
  
  // Dossiers sensibles à masquer dans l'interface
  const sensitiveFolderNames = [
    'Client', 'Transport', 'Permanent', 'Fournisseur', 
    'Lieux', 'Ressources humaines', 'Intermittents'
  ];
  
  // Filtrer les dossiers sensibles
  const filteredFoldersList = foldersList.filter(folder => 
    !sensitiveFolderNames.includes(folder.name)
  );
  
  const foldersByParent = filteredFoldersList.reduce((acc, folder) => {
    const isRoot = folder.parent_id === null || folder.parent_id === undefined || folder.parent_id === 0
    const parentKey = isRoot ? 'root' : String(folder.parent_id)
    if (!acc[parentKey]) acc[parentKey] = []
    acc[parentKey].push(folder)
    return acc
  }, {})

  // Retourne la liste des rentman_id de tous les descendants d'un parent donné (par id interne)
  const getDescendantRentmanIds = (parentInternalId) => {
    const children = foldersByParent[String(parentInternalId)] || []
    const result = []
    for (const child of children) {
      result.push(child.id) // Utiliser l'ID Rentman directement
      result.push(...getDescendantRentmanIds(child.id))
    }
    return result
  }

  const handleToggleCategory = (folder, checked) => {
    const thisRentmanId = folder.id
    const thisInternalId = folder.id
    const cascade = settings.import_include_subfolders
    const all = cascade ? [thisRentmanId, ...getDescendantRentmanIds(thisInternalId)] : [thisRentmanId]
    let next = settings.selected_folders || []
    if (checked) {
      const set = new Set(next)
      for (const id of all) set.add(id)
      next = Array.from(set)
    } else {
      next = next.filter(id => !all.includes(id))
    }
    // Mettre à jour l'état et sauvegarder immédiatement pour persister les coches
    setSettings(prev => {
      const nextSettings = { ...prev, selected_folders: next }
      if (isAuthenticated) {
        try {
          saveMutation.mutate(nextSettings)
        } catch {}
      }
      return nextSettings
    })
  }

  const renderFolderNode = (folder, level = 0) => {
    const children = foldersByParent[String(folder.id)] || []
    return (
      <div key={folder.id}>
        <label className="flex items-center py-2" style={{ paddingLeft: `${level * 16}px` }}>
          <input
            type="checkbox"
            checked={(settings.selected_folders || []).map(String).includes(String(folder.id))}
            onChange={(e) => handleToggleCategory(folder, e.target.checked)}
            className="h-4 w-4 text-orange-600 focus:ring-orange-500 border-gray-300 rounded"
          />
          <span className="ml-3 text-sm text-gray-700">
            {folder.name}
            {folder.level > 0 && (
              <span className="ml-2 text-xs text-gray-500">
                (Niveau {folder.level})
              </span>
            )}
          </span>
        </label>
        {children.map(child => renderFolderNode(child, level + 1))}
      </div>
    )
  }

  // Logs en temps réel
  const [logOperationFilter, setLogOperationFilter] = useState('all')
  const { data: logsData } = useQuery(
    ['rentman-logs', logOperationFilter],
    () => {
      const params = new URLSearchParams()
      if (logOperationFilter !== 'all') params.append('operation', logOperationFilter)
      params.append('limit', 20)
      return apiService.get(`/rentman/logs?${params.toString()}`)
        .then(res => res.data)
    },
    {
      refetchInterval: 2000,
      refetchOnWindowFocus: false,
      enabled: isAuthenticated
    }
  )

  // Sauvegarder les paramètres
  const saveMutation = useMutation(
    (newSettings) => apiService.post('/rentman/settings', newSettings),
    {
      onSuccess: () => {
        toast.success('Paramètres sauvegardés avec succès')
        queryClient.invalidateQueries('rentman-settings')
        queryClient.invalidateQueries('rentman-settings-public')
      },
      onError: (error) => {
        console.error('Save error:', error)
        if (error.response?.status === 401) {
          setIsAuthenticated(false)
          toast.error('Session expirée. Veuillez vous reconnecter en admin.')
        } else if (error.response?.status === 400) {
          toast.error('Données invalides: ' + (error.response.data?.error || 'Paramètres requis'))
        } else {
          toast.error('Erreur lors de la sauvegarde: ' + (error.response?.data?.error || error.message))
        }
      }
    }
  )

  // Tester la connexion
  const testConnectionMutation = useMutation(
    () => apiService.post('/rentman/test-connection').then(res => res.data),
    {
      onSuccess: (data) => {
        if (data?.connected) {
          toast.success('Connexion Rentman réussie')
        } else {
          toast.error('Connexion Rentman échouée')
        }
      },
      onError: (error) => {
        console.error('Erreur test connexion:', error)
        if (error.response?.status === 401) {
          setIsAuthenticated(false)
          toast.error('Session expirée. Veuillez vous reconnecter en admin.')
        } else {
          toast.error('Erreur de connexion Rentman')
        }
      }
    }
  )

  const syncMutation = useMutation(
    () => {
      // Utiliser l'endpoint approprié selon la sélection
      if (settings.selected_folders?.length > 0) {
        const payload = {
          selectedFolders: settings.selected_folders,
          includeSubfolders: settings.import_include_subfolders
        };
;
        return apiService.post('/rentman/sync-selected', payload);
      } else {
        return apiService.post('/rentman/sync', {});
      }
    },
    {
      onSuccess: (data) => {
        const { selectedCategories } = data.data || {};
        const selected = selectedCategories || 0;
        
        if (selected > 0) {
          toast.success('Synchronisation sélective lancée avec succès')
        } else {
          toast.success('Synchronisation complète lancée avec succès')
        }
        
        queryClient.invalidateQueries('rentman-categories')
        queryClient.invalidateQueries('rentman-products')
        queryClient.invalidateQueries('rentman-folders-hierarchy')
      },
      onError: (error) => {
        toast.error('Erreur lors de la synchronisation: ' + (error.response?.data?.message || error.message))
      }
    }
  )

  // Arrêter la synchronisation
  const stopSyncMutation = useMutation(
    () => apiService.post('/rentman/stop-sync'),
    {
      onSuccess: (response) => {
        const data = response.data
        toast.success(data.message || 'Synchronisation arrêtée')
        queryClient.invalidateQueries('rentman-logs')
      },
      onError: (error) => {
        console.error('Stop sync error:', error)
        if (error.response?.status === 401) {
          setIsAuthenticated(false)
          toast.error('Session expirée. Veuillez vous reconnecter en admin.')
        } else {
          toast.error('Erreur lors de l\'arrêt: ' + (error.response?.data?.error || error.message))
        }
      }
    }
  )


  // Vider les logs
  const clearLogsMutation = useMutation(
    () => apiService.delete('/rentman/logs'),
    {
      onSuccess: (data) => {
        toast.success(data.message || 'Logs vidés avec succès')
        queryClient.invalidateQueries('rentman-logs')
      },
      onError: (error) => {
        toast.error(error.response?.data?.message || 'Erreur lors du vidage des logs')
      }
    }
  )

  // Vider le catalogue
  const clearCatalogMutation = useMutation(
    () => apiService.delete('/rentman/catalog'),
    {
      onSuccess: (data) => {
        toast.success(data.message || 'Catalogue vidé avec succès')
        queryClient.invalidateQueries('rentman-products')
        queryClient.invalidateQueries('rentman-categories')
        queryClient.invalidateQueries('rentman-folders-hierarchy')
      },
      onError: (error) => {
        toast.error(error.response?.data?.message || 'Erreur lors du vidage du catalogue')
      }
    }
  )

  // Vider la liste des clients
  const clearClientsMutation = useMutation(
    () => apiService.delete('/rentman/clients'),
    {
      onSuccess: (data) => {
        toast.success(data.message || 'Liste des clients vidée avec succès')
        queryClient.invalidateQueries('users')
        queryClient.invalidateQueries('rentman-reservations')
        queryClient.invalidateQueries('database-stats')
      },
      onError: (error) => {
        toast.error(error.response?.data?.message || 'Erreur lors du vidage de la liste des clients')
      }
    }
  )

  const handleInputChange = (field, value) => {
    setSettings(prev => {
      const newSettings = {
        ...prev,
        [field]: value
      }
      
      // Sauvegarder automatiquement les paramètres de synchronisation et d'import
      if (field.startsWith('auto_sync_') || field === 'import_from_folders' || field === 'import_include_subfolders') {
        setTimeout(() => {
          saveMutation.mutate(newSettings)
        }, 100) // Petit délai pour s'assurer que le state est mis à jour
      }
      
      return newSettings
    })
  }

  const handleSave = () => {
    if (!isAuthenticated) {
      toast.error("Vous devez être connecté pour sauvegarder")
      return
    }
    saveMutation.mutate(settings)
  }

  const handleTestConnection = () => {
    if (!isAuthenticated) {
      toast.error("Vous devez être connecté pour tester")
      return
    }
    if (testConnectionMutation.isLoading) {
      toast.info("Test de connexion en cours...")
      return
    }
    testConnectionMutation.mutate()
  }

  const handleSync = () => {
    if (!isAuthenticated) {
      toast.error("Vous devez être connecté pour synchroniser")
      return
    }
    syncMutation.mutate()
  }

  const handleClearLogs = () => {
    if (window.confirm('Êtes-vous sûr de vouloir vider tous les logs ? Cette action est irréversible.')) {
      clearLogsMutation.mutate()
    }
  }

  const testAdminConnection = async () => {
    try {
      const response = await apiService.post('/auth/login', {
        username: 'admin',
        password: 'admin123'
      })
      if (response.data.token) {
        localStorage.setItem('admin_token', response.data.token)
        setIsAuthenticated(true)
        toast.success('Connexion admin réussie')
        queryClient.invalidateQueries('rentman-settings')
      }
    } catch (error) {
      toast.error('Erreur de connexion admin: ' + (error.response?.data?.error || error.message))
      console.error('Admin login error:', error)
    }
  }

  const tabs = [
    { id: 'general', name: 'Général', icon: Settings },
    { 
      id: 'catalog', 
      name: 'Catalogue', 
      icon: Package,
      subItems: [
        { id: 'catalog-settings', name: 'Produits', icon: Settings },
        { id: 'catalog-images', name: 'Images', icon: Image }
      ]
    },
    { id: 'rentman', name: 'Rentman', icon: Database }
  ]

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-orange-500 mx-auto mb-4"></div>
          <p className="text-gray-600">Chargement des paramètres...</p>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gray-100">
      <div className="container-custom py-8">
        <div className="mb-8">
          <h1 className="text-4xl font-bold mb-4">
            <span className="text-gradient">Maintenance</span>
          </h1>
          <p className="text-xl text-gray-600">Configuration du système</p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-4 gap-8">
          <div className="lg:col-span-1">
            <div className="card">
              <nav className="space-y-2">
                <Link
                  to="/admin"
                  className="w-full flex items-center p-3 rounded-lg transition-colors hover:bg-gray-50 border border-transparent text-gray-700"
                >
                  <LayoutGrid className="h-5 w-5 mr-3 text-gray-500" />
                  <span className="font-medium">Dashboard</span>
                </Link>
                {tabs.map((tab) => {
                  const Icon = tab.icon
                  const isActive = activeTab === tab.id
                  const isRentman = tab.id === 'rentman'
                  const isGeneral = tab.id === 'general'
                  const isCatalog = tab.id === 'catalog'
                  const isExpanded = expandedMenus[tab.id]
                  
                  return (
                    <div key={tab.id}>
                      {tab.subItems ? (
                        // Menu avec sous-éléments (Catalogue)
                        <div>
                          <button
                            onClick={() => toggleMenu(tab.id)}
                            className={`w-full flex items-center justify-between px-4 py-3 text-left rounded-lg transition-colors ${
                              isActive
                                ? 'bg-orange-100 text-orange-700 border border-orange-200'
                                : 'text-gray-600 hover:bg-gray-100'
                            }`}
                          >
                            <div className="flex items-center">
                              <Icon className="h-5 w-5 mr-3" />
                              {tab.name}
                            </div>
                            {isExpanded ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
                          </button>
                          
                          {/* Sous-éléments Catalogue */}
                          {isExpanded && (
                            <div className="ml-6 mt-2 space-y-1 animate-in slide-in-from-top-2 duration-200">
                              {tab.subItems.map((subItem) => {
                                const SubIcon = subItem.icon
                                const isSubActive = activeTab === subItem.id
                                return (
                                  <button
                                    key={subItem.id}
                                    onClick={() => handleTabChange(subItem.id)}
                                    className={`w-full flex items-center px-3 py-2 text-left rounded-lg transition-colors text-sm ${
                                      isSubActive
                                        ? 'bg-orange-50 text-orange-600 border border-orange-100 shadow-sm'
                                        : 'text-gray-500 hover:bg-gray-50'
                                    }`}
                                  >
                                    <SubIcon className="h-4 w-4 mr-2" />
                                    {subItem.name}
                                  </button>
                                )
                              })}
                            </div>
                          )}
                        </div>
                      ) : (
                        // Menu simple
                        <button
                          onClick={() => handleTabChange(tab.id)}
                          className={`w-full flex items-center justify-between px-4 py-3 text-left rounded-lg transition-colors ${
                            isActive
                              ? 'bg-orange-100 text-orange-700 border border-orange-200'
                              : 'text-gray-600 hover:bg-gray-100'
                          }`}
                        >
                          <div className="flex items-center">
                            <Icon className="h-5 w-5 mr-3" />
                            {tab.name}
                          </div>
                          {(isRentman || isGeneral) && (
                            isActive ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />
                          )}
                        </button>
                      )}
                      
                      {/* Sous-onglets Général */}
                      {isGeneral && isActive && (
                        <div className="ml-6 mt-2 space-y-1 animate-in slide-in-from-top-2 duration-200">
                          {[
                            { id: 'company', name: 'Société', icon: User },
                            { id: 'email', name: 'Email (SMTP)', icon: Mail },
                            { id: 'database', name: 'Base de données', icon: Database },
                            { id: 'pages', name: 'Gestion pages', icon: FileText },
                            { id: 'users', name: 'Utilisateurs', icon: Users },
                            { id: 'settings', name: 'Maintenance', icon: Settings }
                          ].map((subTab) => {
                            const SubIcon = subTab.icon
                            return (
                              <button
                                key={subTab.id}
                                onClick={() => setActiveGeneralTab(subTab.id)}
                                className={`w-full flex items-center px-3 py-2 text-left rounded-lg transition-colors text-sm ${
                                  activeGeneralTab === subTab.id
                                    ? 'bg-orange-50 text-orange-600 border border-orange-100 shadow-sm'
                                    : 'text-gray-500 hover:bg-gray-50'
                                }`}
                              >
                                <SubIcon className="h-4 w-4 mr-2" />
                                {subTab.name}
                              </button>
                            )
                          })}
                        </div>
                      )}

                      {/* Sous-onglets Rentman */}
                      {isRentman && isActive && (
                        <div className="ml-6 mt-2 space-y-1 animate-in slide-in-from-top-2 duration-200">
                          {[
                            { id: 'import', name: 'Import produits', icon: Download },
                            { id: 'import_clients', name: 'Import clients', icon: Download },
                            { id: 'export', name: 'Export commandes', icon: Upload },
                            { id: 'export_clients', name: 'Export clients', icon: User },
                            { id: 'caution', name: 'Caution', icon: Shield },
                            { id: 'status', name: 'Statut', icon: Database }
                          ].map((subTab) => {
                            const SubIcon = subTab.icon
                            return (
                              <button
                                key={subTab.id}
                                onClick={() => setActiveRentmanTab(subTab.id)}
                                className={`w-full flex items-center px-3 py-2 text-left rounded-lg transition-colors text-sm ${
                                  activeRentmanTab === subTab.id
                                    ? 'bg-orange-50 text-orange-600 border border-orange-100 shadow-sm'
                                    : 'text-gray-500 hover:bg-gray-50'
                                }`}
                              >
                                <SubIcon className="h-4 w-4 mr-2" />
                                {subTab.name}
                                {activeRentmanTab === subTab.id && (
                                  <div className="ml-auto w-2 h-2 bg-orange-500 rounded-full"></div>
                                )}
                              </button>
                            )
                          })}
                        </div>
                      )}
                    </div>
                  )
                })}
              </nav>
            </div>
          </div>

          {/* Main Content */}
          <div className="lg:col-span-3">
            <div className="card">
              {/* General Settings */}
              {activeTab === 'general' && (
                <div className="space-y-6">
                  <h2 className="text-2xl font-semibold text-gray-900 mb-6">
                    Configuration Générale
                  </h2>
                  
                  {/* Sous-onglets Général - Caché */}
                  {/* <div className="border-b border-gray-200 mb-6">
                    <nav className="-mb-px flex space-x-8">
                      <button
                        onClick={() => setActiveGeneralTab('company')}
                        className={`py-2 px-1 border-b-2 font-medium text-sm ${
                          activeGeneralTab === 'company'
                            ? 'border-orange-500 text-orange-600'
                            : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                        }`}
                      >
                        <User className="h-4 w-4 inline mr-2" />
                        Société
                      </button>
                      <button
                        onClick={() => setActiveGeneralTab('email')}
                        className={`py-2 px-1 border-b-2 font-medium text-sm ${
                          activeGeneralTab === 'email'
                            ? 'border-orange-500 text-orange-600'
                            : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                        }`}
                      >
                        <Mail className="h-4 w-4 inline mr-2" />
                        Email (SMTP)
                      </button>
                      <button
                        onClick={() => setActiveGeneralTab('database')}
                        className={`py-2 px-1 border-b-2 font-medium text-sm ${
                          activeGeneralTab === 'database'
                            ? 'border-orange-500 text-orange-600'
                            : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                        }`}
                      >
                        <Database className="h-4 w-4 inline mr-2" />
                        Base de données
                      </button>
                      <button
                        onClick={() => setActiveGeneralTab('pages')}
                        className={`py-2 px-1 border-b-2 font-medium text-sm ${
                          activeGeneralTab === 'pages'
                            ? 'border-orange-500 text-orange-600'
                            : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                        }`}
                      >
                        <FileText className="h-4 w-4 inline mr-2" />
                        Gestion pages
                      </button>
                      <button
                        onClick={() => setActiveGeneralTab('settings')}
                        className={`py-2 px-1 border-b-2 font-medium text-sm ${
                          activeGeneralTab === 'settings'
                            ? 'border-orange-500 text-orange-600'
                            : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                        }`}
                      >
                        <Settings className="h-4 w-4 inline mr-2" />
                        Maintenance
                      </button>
                    </nav>
                  </div> */}

                  {/* Contenu des sous-onglets */}
                  {activeGeneralTab === 'pages' && (
                    <div className="space-y-6">
                      {/* En-tête */}
                      <div className="flex justify-between items-center">
                        <div>
                          <h3 className="text-lg font-semibold text-gray-900">Gestion des pages</h3>
                          <p className="text-sm text-gray-600">Gérez le contenu des pages statiques du site</p>
                        </div>
                        <button
                          onClick={() => {
                            setEditingPage(null)
                            setPageFormData({
                              title: '',
                              slug: '',
                              content: '',
                              meta_description: '',
                              is_published: false
                            })
                            setShowPageForm(true)
                          }}
                          className="inline-flex items-center px-4 py-2 bg-orange-500 text-white rounded-lg hover:bg-orange-600 transition-colors"
                        >
                          <Plus className="h-4 w-4 mr-2" />
                          Nouvelle page
                        </button>
                      </div>

                      {/* Liste des pages */}
                      <div className="bg-white rounded-lg shadow-sm border border-gray-200">
                        <div className="p-6">
                          <div className="space-y-4">
                            {/* Pages depuis l'API ou pages prédéfinies par défaut */}
                            {(pages.length > 0 ? pages : [
                              { slug: 'mentions-legales', title: 'Mentions légales', description: 'Informations légales de l\'entreprise' },
                              { slug: 'politique-confidentialite', title: 'Politique de confidentialité', description: 'Protection des données personnelles' },
                              { slug: 'cgv', title: 'CGV', description: 'Conditions générales de vente' },
                              { slug: 'contact', title: 'Contact', description: 'Informations de contact' }
                            ]).map((page) => (
                              <div key={page.slug} className="flex items-center justify-between p-4 border border-gray-200 rounded-lg">
                                <div className="flex-1">
                                  <h4 className="font-medium text-gray-900">{page.title}</h4>
                                  <p className="text-sm text-gray-600">{page.description}</p>
                                  <p className="text-xs text-gray-500">Slug: /{page.slug}</p>
                                </div>
                                <div className="flex items-center space-x-2">
                                  <button
                                    onClick={async () => {
                                      // Chercher la page dans la liste chargée depuis l'API
                                      const existingPage = pages.find(p => p.slug === page.slug)
                                      if (existingPage) {
                                        // Page existe en DB, utiliser ses données
                                        setEditingPage(existingPage)
                                        setPageFormData({
                                          title: existingPage.title || '',
                                          slug: existingPage.slug || '',
                                          content: existingPage.content || '',
                                          meta_description: existingPage.meta_description || '',
                                          is_published: existingPage.is_published || false
                                        })
                                      } else {
                                        // Page n'existe pas encore en DB, utiliser les données par défaut
                                        setEditingPage({ slug: page.slug })
                                        setPageFormData({
                                          title: page.title || '',
                                          slug: page.slug || '',
                                          content: '',
                                          meta_description: '',
                                          is_published: false
                                        })
                                      }
                                      setShowPageForm(true)
                                    }}
                                    className="p-2 text-gray-600 hover:text-orange-600 transition-colors"
                                    title="Modifier"
                                  >
                                    <Edit className="h-4 w-4" />
                                  </button>
                                  <a
                                    href={`/${page.slug}`}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="p-2 text-gray-600 hover:text-blue-600 transition-colors"
                                    title="Voir la page"
                                  >
                                    <ExternalLink className="h-4 w-4" />
                                  </a>
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      </div>

                      {/* Formulaire de page */}
                      {showPageForm && (
                        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                          <h4 className="text-lg font-semibold text-gray-900 mb-4">
                            {editingPage ? 'Modifier la page' : 'Nouvelle page'}
                          </h4>
                          
                          <div className="space-y-4">
                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-2">
                                Titre
                              </label>
                              <input
                                type="text"
                                value={pageFormData.title}
                                onChange={(e) => setPageFormData({ ...pageFormData, title: e.target.value })}
                                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                                placeholder="Titre de la page"
                              />
                            </div>
                            
                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-2">
                                Slug (URL)
                              </label>
                              <input
                                type="text"
                                value={pageFormData.slug}
                                onChange={(e) => setPageFormData({ ...pageFormData, slug: e.target.value })}
                                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                                placeholder="slug-de-la-page"
                              />
                            </div>
                            
                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-2">
                                Description meta
                              </label>
                              <textarea
                                rows={2}
                                value={pageFormData.meta_description}
                                onChange={(e) => setPageFormData({ ...pageFormData, meta_description: e.target.value })}
                                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                                placeholder="Description pour le SEO"
                              />
                            </div>
                            
                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-2">
                                Contenu
                              </label>
                              <textarea
                                rows={10}
                                value={pageFormData.content}
                                onChange={(e) => setPageFormData({ ...pageFormData, content: e.target.value })}
                                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                                placeholder="Contenu de la page (HTML autorisé)"
                              />
                            </div>
                            
                            <div className="flex items-center">
                              <input
                                type="checkbox"
                                id="is_published"
                                checked={pageFormData.is_published}
                                onChange={(e) => setPageFormData({ ...pageFormData, is_published: e.target.checked })}
                                className="h-4 w-4 text-orange-600 focus:ring-orange-500 border-gray-300 rounded"
                              />
                              <label htmlFor="is_published" className="ml-2 block text-sm text-gray-700">
                                Page publiée
                              </label>
                            </div>
                          </div>
                          
                          <div className="flex justify-end space-x-3 mt-6">
                            <button
                              onClick={() => setShowPageForm(false)}
                              className="px-4 py-2 text-gray-600 hover:text-gray-800 transition-colors"
                            >
                              Annuler
                            </button>
                            <button
                              onClick={() => {
                                if (!pageFormData.title || !pageFormData.slug) {
                                  toast.error('Le titre et le slug sont requis')
                                  return
                                }
                                savePageMutation.mutate(pageFormData)
                              }}
                              disabled={savePageMutation.isLoading}
                              className="px-4 py-2 bg-orange-500 text-white rounded-lg hover:bg-orange-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                              {savePageMutation.isLoading ? 'Sauvegarde...' : 'Sauvegarder'}
                            </button>
                          </div>
                        </div>
                      )}
                    </div>
                  )}


                  {activeGeneralTab === 'settings' && (
                    <div className="space-y-6">
                      {/* Section Maintenance */}
                      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                        <div className="flex items-center mb-4">
                          <Settings className="h-6 w-6 text-orange-500 mr-2" />
                          <h3 className="text-lg font-semibold text-gray-900">
                            Mode Maintenance
                          </h3>
                        </div>
                        
                        <div className="space-y-4">
                          {/* Toggle Maintenance */}
                          <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                            <div>
                              <h4 className="font-medium text-gray-900">Activer la maintenance</h4>
                              <p className="text-sm text-gray-600">
                                Bloquer l'accès au site pour tous les utilisateurs sauf les administrateurs
                              </p>
                            </div>
                            <label className="relative inline-flex items-center cursor-pointer">
                              <input
                                type="checkbox"
                                checked={settings.maintenance_mode || false}
                                onChange={(e) => setSettings(prev => ({ 
                                  ...prev, 
                                  maintenance_mode: e.target.checked 
                                }))}
                                className="sr-only peer"
                              />
                              <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-orange-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-orange-500"></div>
                            </label>
                          </div>

                          {/* Message de maintenance */}
                          <div className="space-y-2">
                            <label className="block text-sm font-medium text-gray-700">
                              Message de maintenance
                            </label>
                            <textarea
                              value={settings.maintenance_message || ''}
                              onChange={(e) => setSettings(prev => ({ 
                                ...prev, 
                                maintenance_message: e.target.value 
                              }))}
                              placeholder="Site en maintenance. Nous revenons bientôt !"
                              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                              rows={3}
                            />
                            <p className="text-xs text-gray-500">
                              Message affiché aux utilisateurs pendant la maintenance
                            </p>
                          </div>

                          {/* Image de bannière */}
                          <div className="space-y-2">
                            <label className="block text-sm font-medium text-gray-700">
                              Image de bannière (optionnelle)
                            </label>
                            <input
                              type="text"
                              value={settings.maintenance_banner_image || ''}
                              onChange={(e) => setSettings(prev => ({ 
                                ...prev, 
                                maintenance_banner_image: e.target.value 
                              }))}
                              placeholder="/images/hero-cinema.jpg"
                              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                            />
                            <p className="text-xs text-gray-500">
                              Chemin vers l'image à afficher en haut de la page de maintenance
                            </p>
                          </div>

                          {/* Aperçu */}
                          {settings.maintenance_mode && (
                            <div className="mt-4 p-4 bg-orange-50 border border-orange-200 rounded-lg">
                              <h5 className="font-medium text-orange-800 mb-2">Aperçu de la page de maintenance :</h5>
                              <div className="text-sm text-orange-700">
                                <p><strong>Message :</strong> {settings.maintenance_message || 'Site en maintenance. Nous revenons bientôt !'}</p>
                                {settings.maintenance_banner_image && (
                                  <p><strong>Image :</strong> {settings.maintenance_banner_image}</p>
                                )}
                              </div>
                            </div>
                          )}

                          {/* Bouton de sauvegarde */}
                          <div className="flex justify-end pt-4">
                            <button
                              onClick={handleSaveSettings}
                              disabled={isSaving}
                              className="inline-flex items-center px-4 py-2 bg-orange-500 text-white rounded-lg hover:bg-orange-600 focus:outline-none focus:ring-2 focus:ring-orange-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                              {isSaving ? (
                                <>
                                  <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                                  Sauvegarde...
                                </>
                              ) : (
                                <>
                                  <Save className="h-4 w-4 mr-2" />
                                  Sauvegarder
                                </>
                              )}
                            </button>
                          </div>
                        </div>
                      </div>

                      {/* Section Informations */}
                      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                        <div className="flex items-start">
                          <AlertCircle className="h-5 w-5 text-blue-500 mr-2 mt-0.5" />
                          <div className="text-sm text-blue-700">
                            <p className="font-medium mb-1">Mode Maintenance activé :</p>
                            <ul className="list-disc list-inside space-y-1">
                              <li>Le site sera inaccessible aux utilisateurs normaux</li>
                              <li><strong>Tous les utilisateurs non-admin seront automatiquement déconnectés</strong></li>
                              <li>Seuls les administrateurs connectés pourront accéder au site</li>
                              <li>La page de maintenance s'affichera avec votre message personnalisé</li>
                              <li>L'image de bannière sera affichée si spécifiée</li>
                              <li>Les paniers des utilisateurs seront vidés</li>
                            </ul>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}

                  {activeGeneralTab === 'database' && (
                    <div className="space-y-6">
                      {/* Statistiques de la base de données */}
                      {dbStatsLoading ? (
                        <div className="text-center py-8">
                          <RefreshCw className="h-6 w-6 animate-spin text-orange-500 mx-auto mb-2" />
                          <p className="text-gray-600">Chargement des statistiques...</p>
                        </div>
                      ) : (
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
                          {/* Produits */}
                          <div className="card">
                            <div className="flex items-center justify-between mb-4">
                              <h3 className="text-lg font-semibold text-gray-900 flex items-center">
                                <Package className="h-5 w-5 mr-2 text-orange-500" />
                                Produits
                              </h3>
                              <span className="text-2xl font-bold text-orange-600">
                                {dbStats?.data?.products?.total || 0}
                              </span>
                            </div>
                            <div className="space-y-2 text-sm">
                              <div className="flex justify-between">
                                <span className="text-gray-600">Disponibles:</span>
                                <span className="font-medium text-green-600">
                                  {dbStats?.data?.products?.available || 0}
                                </span>
                              </div>
                              <div className="flex justify-between">
                                <span className="text-gray-600">Avec images:</span>
                                <span className="font-medium text-blue-600">
                                  {dbStats?.data?.products?.with_images || 0}
                                </span>
                              </div>
                            </div>
                          </div>

                          {/* Catégories */}
                          <div className="card">
                            <div className="flex items-center justify-between mb-4">
                              <h3 className="text-lg font-semibold text-gray-900 flex items-center">
                                <Folder className="h-5 w-5 mr-2 text-orange-500" />
                                Catégories
                              </h3>
                              <span className="text-2xl font-bold text-orange-600">
                                {dbStats?.data?.categories?.total || 0}
                              </span>
                            </div>
                            <div className="space-y-2 text-sm">
                              <div className="flex justify-between">
                                <span className="text-gray-600">Principales:</span>
                                <span className="font-medium text-blue-600">
                                  {dbStats?.data?.categories?.root_categories || 0}
                                </span>
                              </div>
                              <div className="flex justify-between">
                                <span className="text-gray-600">Sous-catégories:</span>
                                <span className="font-medium text-purple-600">
                                  {dbStats?.data?.categories?.sub_categories || 0}
                                </span>
                              </div>
                            </div>
                          </div>

                          {/* Réservations */}
                          <div className="card">
                            <div className="flex items-center justify-between mb-4">
                              <h3 className="text-lg font-semibold text-gray-900 flex items-center">
                                <Calendar className="h-5 w-5 mr-2 text-orange-500" />
                                Réservations
                              </h3>
                              <span className="text-2xl font-bold text-orange-600">
                                {dbStats?.data?.reservations?.total || 0}
                              </span>
                            </div>
                            <div className="space-y-2 text-sm">
                              <div className="flex justify-between">
                                <span className="text-gray-600">Demande envoyée:</span>
                                <span className="font-medium text-orange-600">
                                  {dbStats?.data?.reservations?.demande_envoyee || 0}
                                </span>
                              </div>
                              <div className="flex justify-between">
                                <span className="text-gray-600">Confirmées:</span>
                                <span className="font-medium text-green-600">
                                  {dbStats?.data?.reservations?.confirmed || 0}
                                </span>
                              </div>
                            </div>
                          </div>
                        </div>
                      )}

                      {/* Structure des tables */}
                      <div className="card">
                        <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                          <Table className="h-5 w-5 mr-2 text-orange-500" />
                          Structure des Tables
                        </h3>
                        {structureLoading ? (
                          <div className="text-center py-8">
                            <RefreshCw className="h-6 w-6 animate-spin text-orange-500 mx-auto mb-2" />
                            <p className="text-gray-600">Chargement de la structure...</p>
                          </div>
                        ) : (
                          <div className="space-y-4">
                            {tableStructure?.data?.tables?.map((tableName) => {
                              const structure = tableStructure.data.structures[tableName]
                              const isExpanded = expandedTables[tableName]
                              
                              return (
                                <div key={tableName} className="border border-gray-200 rounded-lg">
                                  <button
                                    onClick={() => toggleTable(tableName)}
                                    className="w-full px-4 py-3 text-left flex items-center justify-between hover:bg-gray-50 transition-colors"
                                  >
                                    <div className="flex items-center">
                                      {isExpanded ? (
                                        <ChevronDown className="h-4 w-4 mr-2 text-gray-500" />
                                      ) : (
                                        <ChevronRight className="h-4 w-4 mr-2 text-gray-500" />
                                      )}
                                      <span className="font-medium text-gray-900">{tableName}</span>
                                      <span className="ml-2 text-sm text-gray-500">
                                        ({structure?.columns?.length || 0} colonnes)
                                      </span>
                                    </div>
                                    <div className="flex items-center space-x-2">
                                      {structure?.foreign_keys?.length > 0 && (
                                        <span className="flex items-center text-xs text-blue-600">
                                          <Key className="h-3 w-3 mr-1" />
                                          {structure.foreign_keys.length} FK
                                        </span>
                                      )}
                                      {structure?.indexes?.length > 0 && (
                                        <span className="flex items-center text-xs text-green-600">
                                          <Hash className="h-3 w-3 mr-1" />
                                          {structure.indexes.length} index
                                        </span>
                                      )}
                                    </div>
                                  </button>
                                  
                                  {isExpanded && (
                                    <div className="border-t border-gray-200 p-4 bg-gray-50">
                                      {/* Colonnes */}
                                      <div className="mb-4">
                                        <h4 className="font-medium text-gray-900 mb-2">Colonnes</h4>
                                        <div className="overflow-x-auto">
                                          <table className="min-w-full text-sm">
                                            <thead>
                                              <tr className="border-b border-gray-200">
                                                <th className="text-left py-2 pr-4 font-medium text-gray-700">Nom</th>
                                                <th className="text-left py-2 pr-4 font-medium text-gray-700">Type</th>
                                                <th className="text-left py-2 pr-4 font-medium text-gray-700">Nullable</th>
                                                <th className="text-left py-2 font-medium text-gray-700">Défaut</th>
                                              </tr>
                                            </thead>
                                            <tbody>
                                              {structure?.columns?.map((column, index) => (
                                                <tr key={index} className="border-b border-gray-100">
                                                  <td className="py-2 pr-4 font-mono text-xs">
                                                    {column.column_name}
                                                  </td>
                                                  <td className="py-2 pr-4 text-gray-600">
                                                    {getDataTypeDisplay(column)}
                                                  </td>
                                                  <td className="py-2 pr-4">
                                                    <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs ${
                                                      column.is_nullable === 'YES' 
                                                        ? 'bg-yellow-100 text-yellow-800' 
                                                        : 'bg-red-100 text-red-800'
                                                    }`}>
                                                      {column.is_nullable === 'YES' ? 'Oui' : 'Non'}
                                                    </span>
                                                  </td>
                                                  <td className="py-2 text-gray-500 font-mono text-xs">
                                                    {column.column_default || '-'}
                                                  </td>
                                                </tr>
                                              ))}
                                            </tbody>
                                          </table>
                                        </div>
                                      </div>

                                      {/* Clés étrangères */}
                                      {structure?.foreign_keys?.length > 0 && (
                                        <div className="mb-4">
                                          <h4 className="font-medium text-gray-900 mb-2 flex items-center">
                                            <Key className="h-4 w-4 mr-1 text-blue-500" />
                                            Clés étrangères
                                          </h4>
                                          <div className="space-y-2">
                                            {structure.foreign_keys.map((fk, index) => (
                                              <div key={index} className="bg-blue-50 p-3 rounded-lg">
                                                <div className="font-mono text-sm">
                                                  <span className="text-blue-800">{fk.column_name}</span>
                                                  <span className="text-gray-500 mx-2">→</span>
                                                  <span className="text-blue-600">
                                                    {fk.foreign_table_name}.{fk.foreign_column_name}
                                                  </span>
                                                </div>
                                                <div className="text-xs text-gray-500 mt-1">
                                                  {fk.constraint_name}
                                                </div>
                                              </div>
                                            ))}
                                          </div>
                                        </div>
                                      )}

                                      {/* Index */}
                                      {structure?.indexes?.length > 0 && (
                                        <div>
                                          <h4 className="font-medium text-gray-900 mb-2 flex items-center">
                                            <Hash className="h-4 w-4 mr-1 text-green-500" />
                                            Index
                                          </h4>
                                          <div className="space-y-2">
                                            {structure.indexes.map((index, indexIndex) => (
                                              <div key={indexIndex} className="bg-green-50 p-3 rounded-lg">
                                                <div className="font-mono text-sm text-green-800">
                                                  {index.indexname}
                                                </div>
                                                <div className="text-xs text-gray-600 mt-1 font-mono">
                                                  {index.indexdef}
                                                </div>
                                              </div>
                                            ))}
                                          </div>
                                        </div>
                                      )}
                                    </div>
                                  )}
                                </div>
                              )
                            })}
                          </div>
                        )}
                      </div>
                      
                      {/* Save Button pour les paramètres généraux */}
                      <div className="mt-8 flex justify-between items-center">
                        {isAuthenticated && (
                          <button onClick={handleSave} disabled={saveMutation.isLoading} className="btn-primary flex items-center">
                            {saveMutation.isLoading ? (
                              <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                            ) : (
                              <Save className="h-4 w-4 mr-2" />
                            )}
                            Sauvegarder les paramètres
                          </button>
                        )}
                      </div>
                    </div>
                  )}

                  {activeGeneralTab === 'company' && (
                    <div className="space-y-6">
                      <div className="border border-gray-200 rounded-lg p-6">
                        <h3 className="text-lg font-semibold text-gray-900 mb-4">Section 1 — Coordonnées</h3>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">Nom société</label>
                            <input className="input-field" value={company.name} onChange={e => handleCompanyChange('name', e.target.value)} />
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">Téléphone fixe</label>
                            <input className="input-field" value={company.phone_landline || ''} onChange={e => handleCompanyChange('phone_landline', e.target.value)} />
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">Téléphone mobile</label>
                            <input className="input-field" value={company.phone_mobile || ''} onChange={e => handleCompanyChange('phone_mobile', e.target.value)} />
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">Email</label>
                            <input className="input-field" type="email" value={company.email || ''} onChange={e => handleCompanyChange('email', e.target.value)} />
                          </div>
                          <div className="md:col-span-2">
                            <label className="block text-sm font-medium text-gray-700 mb-2">Adresse complète</label>
                            <textarea className="input-field" rows={3} value={company.address || ''} onChange={e => handleCompanyChange('address', e.target.value)} />
                          </div>
                        </div>
                      </div>

                      {/* Section Logo */}
                      <div className="border border-gray-200 rounded-lg p-6">
                        <h3 className="text-lg font-semibold text-gray-900 mb-4">Section 2 — Logo de l'entreprise</h3>
                        
                        {/* Aperçu du logo actuel */}
                        {company.logo_path && (
                          <div className="mb-4">
                            <label className="block text-sm font-medium text-gray-700 mb-2">Logo actuel</label>
                            <div className="flex items-center space-x-4">
                              <img 
                                src={getImageUrl(company.logo_path)}
                                alt="Logo actuel"
                                className="h-16 w-auto object-contain border border-gray-200 rounded-lg p-2 bg-white"
                                onError={(e) => {
                                  e.target.style.display = 'none'
                                }}
                              />
                              <div className="text-sm text-gray-600">
                                <p>Chemin: {company.logo_path}</p>
                                <p className="text-xs text-gray-500">Affiché dans le header et footer</p>
                              </div>
                            </div>
                          </div>
                        )}
                        
                        {/* Upload de nouveau logo */}
                        <div className="space-y-4">
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">Nouveau logo</label>
                            <input
                              type="file"
                              accept="image/*"
                              onChange={(e) => handleLogoFileSelect(e.target.files[0])}
                              className="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-medium file:bg-orange-50 file:text-orange-700 hover:file:bg-orange-100"
                            />
                            <p className="text-xs text-gray-500 mt-1">
                              Formats acceptés: JPG, PNG, GIF, WebP (max 5MB)
                            </p>
                          </div>
                          
                          {logoFile && (
                            <div className="p-3 bg-orange-50 border border-orange-200 rounded-lg">
                              <div className="text-sm text-orange-800">
                                <p className="font-medium">Fichier sélectionné: {logoFile.name}</p>
                                <p>Taille: {(logoFile.size / 1024 / 1024).toFixed(2)} MB</p>
                                <p className="text-xs mt-1">Le logo sera uploadé lors de la sauvegarde des paramètres.</p>
                              </div>
                            </div>
                          )}
                        </div>
                      </div>

                      <div className="border border-gray-200 rounded-lg p-6">
                        <h3 className="text-lg font-semibold text-gray-900 mb-4">Section 3 — Format monétaire</h3>
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">Devise</label>
                            <input className="input-field" value={company.currency} onChange={e => handleCompanyChange('currency', e.target.value)} />
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">Position devise</label>
                            <select className="input-field" value={company.currency_position} onChange={e => handleCompanyChange('currency_position', e.target.value)}>
                              <option value="left">Gauche</option>
                              <option value="right">Droite</option>
                            </select>
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">Séparateur milliers</label>
                            <input className="input-field" value={company.thousands_separator} onChange={e => handleCompanyChange('thousands_separator', e.target.value)} />
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">Séparateur décimal</label>
                            <input className="input-field" value={company.decimal_separator} onChange={e => handleCompanyChange('decimal_separator', e.target.value)} />
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">Nombre de décimales</label>
                            <input className="input-field" type="number" min="0" max="6" value={company.decimal_places} onChange={e => handleCompanyChange('decimal_places', Number(e.target.value))} />
                          </div>
                        </div>
                      </div>
                      
                      {/* Bouton Enregistrer tout en bas de la page société */}
                      <div className="mt-8 flex justify-end">
                        <button 
                          onClick={() => saveCompanyMutation.mutate()} 
                          disabled={saveCompanyMutation.isLoading || isUploadingLogo}
                          className="btn-primary disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          {saveCompanyMutation.isLoading || isUploadingLogo ? (
                            <>
                              <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                              {isUploadingLogo ? 'Upload du logo...' : 'Sauvegarde...'}
                            </>
                          ) : (
                            <>
                              <Save className="h-4 w-4 mr-2" />
                              {logoFile ? 'Enregistrer et uploader le logo' : 'Enregistrer'}
                            </>
                          )}
                        </button>
                      </div>
                    </div>
                  )}

                  {activeGeneralTab === 'email' && (
                    <div className="space-y-6">
                      <div className="border border-gray-200 rounded-lg p-6">
                        <h3 className="text-lg font-semibold text-gray-900 mb-4">Paramètres SMTP</h3>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">Hôte SMTP</label>
                            <input id="smtp_host" className="input-field" placeholder="smtp.example.com" value={settings.smtp_host} onChange={(e)=>handleInputChange('smtp_host', e.target.value)} />
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">Port</label>
                            <input id="smtp_port" className="input-field" placeholder="587" value={settings.smtp_port} onChange={(e)=>handleInputChange('smtp_port', e.target.value)} />
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">Utilisateur</label>
                            <input id="smtp_user" className="input-field" placeholder="utilisateur@example.com" value={settings.smtp_user} onChange={(e)=>handleInputChange('smtp_user', e.target.value)} />
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">Mot de passe</label>
                            <input id="smtp_pass" type="password" className="input-field" placeholder="••••••••" value={settings.smtp_pass} onChange={(e)=>handleInputChange('smtp_pass', e.target.value)} />
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">Expiration lien reset (minutes)</label>
                            <input
                              id="smtp_reset_exp_minutes"
                              type="number"
                              min="1"
                              className="input-field"
                              placeholder="5"
                              value={settings.smtp_reset_exp_minutes}
                              onChange={(e)=>handleInputChange('smtp_reset_exp_minutes', Number(e.target.value))}
                            />
                          </div>
                          <div className="md:col-span-2">
                            <label className="block text-sm font-medium text-gray-700 mb-2">Destinataire test (optionnel)</label>
                            <input id="smtp_to" className="input-field" placeholder="email de test" />
                          </div>
                        </div>
                        
                        {/* Section Formulaire de contact */}
                        <div className="mt-8 pt-6 border-t border-gray-200">
                          <h4 className="text-md font-semibold text-gray-900 mb-4">Formulaire de contact</h4>
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-2">Email de destination</label>
                              <input 
                                id="contact_email_destination" 
                                className="input-field" 
                                placeholder="contact@flightcasefwi.com" 
                                value={settings.contact_email_destination} 
                                onChange={(e)=>handleInputChange('contact_email_destination', e.target.value)} 
                              />
                              <p className="text-xs text-gray-500 mt-1">Email où seront envoyés les messages du formulaire de contact</p>
                            </div>
                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-2">Sujet par défaut</label>
                              <input 
                                id="contact_email_subject" 
                                className="input-field" 
                                placeholder="[Contact FlightCase] Nouveau message" 
                                value={settings.contact_email_subject} 
                                onChange={(e)=>handleInputChange('contact_email_subject', e.target.value)} 
                              />
                              <p className="text-xs text-gray-500 mt-1">Sujet utilisé pour les emails de contact (le sujet du visiteur sera ajouté)</p>
                            </div>
                          </div>
                        </div>
                        <div className="mt-6 flex flex-col sm:flex-row gap-3">
                          <button
                            onClick={async () => {
                              try {
                                await apiService.post('/rentman/settings', settings)
                                toast.success('Paramètres SMTP sauvegardés avec succès')
                              } catch (e) {
                                toast.error(e.response?.data?.error || 'Erreur lors de la sauvegarde')
                              }
                            }}
                            className="btn-primary flex-1"
                          >
                            💾 Sauvegarder les paramètres SMTP
                          </button>
                          <button
                            onClick={async () => {
                              const host = settings.smtp_host
                              const port = settings.smtp_port
                              const user = settings.smtp_user
                              const pass = settings.smtp_pass
                              const to = document.getElementById('smtp_to').value
                              try {
                                const res = await apiService.post('/emails/test-smtp', { host, port, user, pass, to })
                                toast.success('Email de test envoyé')
                              } catch (e) {
                                toast.error(e.response?.data?.error || 'Erreur envoi test')
                              }
                            }}
                            className="btn-secondary flex-1"
                          >
                            📧 Envoyer un email de test
                          </button>
                        </div>

                        
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* Catalogue */}
              {(activeTab === 'catalog' || activeTab === 'catalog-settings') && (
                <div className="space-y-6">
                  <h2 className="text-2xl font-semibold text-gray-900 mb-6">Catalogue</h2>
                  <div className="border border-gray-200 rounded-lg p-6">
                    <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                      <Database className="h-5 w-5 mr-2 text-orange-500" />
                      Paramètres d'affichage du catalogue
                    </h3>
                    <div className="space-y-4">
                      <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                        <div>
                          <h4 className="font-medium text-gray-900">Autoriser l'affichage du prix</h4>
                          <p className="text-sm text-gray-600">
                            Désactive/active l'affichage des prix sur tout le site
                          </p>
                        </div>
                        <label className="relative inline-flex items-center cursor-pointer">
                          <input
                            type="checkbox"
                            checked={Boolean(settings.show_regular_price)}
                            onChange={(e) => {
                              const val = e.target.checked
                              const next = { ...settings, show_regular_price: val }
                              setSettings(next)
                              saveMutation.mutate(next)
                            }}
                            className="sr-only peer"
                          />
                          <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-orange-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-orange-500"></div>
                        </label>
                      </div>
                      <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                        <div>
                          <h4 className="font-medium text-gray-900">Autoriser l'affichage de la disponibilité</h4>
                          <p className="text-sm text-gray-600">
                            Désactive/active l'état de disponibilité sur tout le site
                          </p>
                        </div>
                        <label className="relative inline-flex items-center cursor-pointer">
                          <input
                            type="checkbox"
                            checked={Boolean(settings.show_availability)}
                            onChange={(e) => {
                              const val = e.target.checked
                              const next = { ...settings, show_availability: val }
                              setSettings(next)
                              saveMutation.mutate(next)
                            }}
                            className="sr-only peer"
                          />
                          <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-orange-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-orange-500"></div>
                        </label>
                      </div>

                      <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                        <div>
                          <h4 className="font-medium text-gray-900">Autoriser l'affichage de la description</h4>
                          <p className="text-sm text-gray-600">
                            Désactive/active l'état de description du catalogue
                          </p>
                        </div>
                        <label className="relative inline-flex items-center cursor-pointer">
                          <input
                            type="checkbox"
                            checked={Boolean(settings.show_product_description)}
                            onChange={(e) => {
                              const val = e.target.checked
                              const next = { ...settings, show_product_description: val }
                              setSettings(next)
                              saveMutation.mutate(next)
                            }}
                            className="sr-only peer"
                          />
                          <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-orange-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-orange-500"></div>
                        </label>
                      </div>
                      <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                        <div>
                          <h4 className="font-medium text-gray-900">Autoriser la description (fiche produit)</h4>
                          <p className="text-sm text-gray-600">
                            Active/désactive l'affichage de la description sur la page produit
                          </p>
                        </div>
                        <label className="relative inline-flex items-center cursor-pointer">
                          <input
                            type="checkbox"
                            checked={Boolean(settings.show_product_description_product)}
                            onChange={(e) => {
                              const val = e.target.checked
                              const next = { ...settings, show_product_description_product: val }
                              setSettings(next)
                              saveMutation.mutate(next)
                            }}
                            className="sr-only peer"
                          />
                          <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-orange-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-orange-500"></div>
                        </label>
                      </div>
                      {/* Sauvegarde non nécessaire: switches enregistrent immédiatement */}
                    </div>
                  </div>

                  {/* Vider le catalogue */}
                  <div className="border border-gray-200 rounded-lg p-6">
                    <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                      <Trash2 className="h-5 w-5 mr-2 text-red-500" />
                      Vider le catalogue
                    </h3>
                    
                    <div className="space-y-4">
                      <p className="text-sm text-gray-600">
                        Cette action supprimera tous les produits et catégories de votre base de données locale. 
                        Cette action est irréversible.
                      </p>
                      
                      <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                        <div className="flex items-start">
                          <AlertCircle className="h-5 w-5 text-red-500 mr-3 mt-0.5 flex-shrink-0" />
                          <div>
                            <h4 className="text-sm font-medium text-red-800 mb-1">
                              Attention
                            </h4>
                            <p className="text-sm text-red-700">
                              Cette action supprimera définitivement :
                            </p>
                            <ul className="text-sm text-red-700 mt-2 list-disc list-inside space-y-1">
                              <li>Tous les produits importés de Rentman</li>
                              <li>Toutes les catégories</li>
                              <li>Toutes les images téléchargées (originales et optimisées)</li>
                              <li>L'historique des synchronisations</li>
                            </ul>
                          </div>
                        </div>
                      </div>

                      {!isAuthenticated ? (
                        <div className="text-sm text-gray-600 bg-yellow-50 p-3 rounded-lg border border-yellow-200">
                          <AlertCircle className="h-4 w-4 inline mr-2" />
                          Vous devez être connecté en admin pour vider le catalogue.
                          <a href="/admin/login" className="text-orange-600 hover:text-orange-800 ml-1 underline">
                            Se connecter
                          </a>
                        </div>
                      ) : (
                        <div className="flex gap-3">
                          <button
                            onClick={() => {
                              if (window.confirm('Êtes-vous sûr de vouloir vider complètement le catalogue ? Cette action est irréversible et supprimera tous les produits, catégories et images.')) {
                                if (window.confirm('Dernière confirmation : Voulez-vous vraiment supprimer TOUT le catalogue ?')) {
                                  clearCatalogMutation.mutate()
                                }
                              }
                            }}
                            disabled={clearCatalogMutation.isLoading}
                            className="btn-outline flex items-center text-red-600 border-red-300 hover:bg-red-50"
                          >
                            {clearCatalogMutation.isLoading ? (
                              <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                            ) : (
                              <Trash2 className="h-4 w-4 mr-2" />
                            )}
                            Vider le catalogue
                          </button>
                          
                          <button
                            onClick={() => {
                              if (window.confirm('Êtes-vous sûr de vouloir vider la liste des clients ? Cette action supprimera tous les clients et leurs réservations.')) {
                                if (window.confirm('Dernière confirmation : Voulez-vous vraiment supprimer TOUS les clients ?')) {
                                  clearClientsMutation.mutate()
                                }
                              }
                            }}
                            disabled={clearClientsMutation.isLoading}
                            className="btn-outline flex items-center text-orange-600 border-orange-300 hover:bg-orange-50"
                          >
                            {clearClientsMutation.isLoading ? (
                              <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                            ) : (
                              <Users className="h-4 w-4 mr-2" />
                            )}
                            Vider liste client
                          </button>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              )}

              {/* Images du catalogue */}
              {activeTab === 'catalog-images' && (
                <div className="space-y-6">
                  <h2 className="text-2xl font-semibold text-gray-900 mb-6">Images du catalogue</h2>
                  
                  {/* Statistiques */}
                  {dbStatsLoading ? (
                    <div className="text-center py-8">
                      <RefreshCw className="h-6 w-6 animate-spin text-orange-500 mx-auto mb-2" />
                      <p className="text-gray-600">Chargement des statistiques...</p>
                    </div>
                  ) : (
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                      {/* Première ligne - Images */}
                      <div className="bg-white border border-gray-200 rounded-lg p-6">
                        <div className="flex items-center">
                          <div className="p-2 bg-orange-100 rounded-lg">
                            <Image className="h-6 w-6 text-orange-600" />
                          </div>
                          <div className="ml-4">
                            <p className="text-sm font-medium text-gray-600">Images totales</p>
                            <p className="text-2xl font-semibold text-gray-900">
                              {dbStats?.data?.products?.with_images || 0}
                            </p>
                          </div>
                        </div>
                      </div>
                      
                      <div className="bg-white border border-gray-200 rounded-lg p-6">
                        <div className="flex items-center">
                          <div className="p-2 bg-blue-100 rounded-lg">
                            <Package className="h-6 w-6 text-blue-600" />
                          </div>
                          <div className="ml-4">
                            <p className="text-sm font-medium text-gray-600">Produits avec images</p>
                            <p className="text-2xl font-semibold text-gray-900">
                              {dbStats?.data?.products?.with_images || 0}
                            </p>
                          </div>
                        </div>
                      </div>
                    
                      <div className="bg-white border border-gray-200 rounded-lg p-6">
                        <div className="flex items-center">
                          <div className="p-2 bg-purple-100 rounded-lg">
                            <Layers className="h-6 w-6 text-purple-600" />
                          </div>
                          <div className="ml-4">
                            <p className="text-sm font-medium text-gray-600">Produits multi-images</p>
                            <p className="text-2xl font-semibold text-gray-900">0</p>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Deuxième ligne - Poids */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="bg-white border border-gray-200 rounded-lg p-6">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center">
                          <div className="p-2 bg-red-100 rounded-lg">
                            <Database className="h-6 w-6 text-red-600" />
                          </div>
                          <div className="ml-4">
                            <p className="text-sm font-medium text-gray-600">Poids original</p>
                            <p className="text-2xl font-semibold text-gray-900">
                              {isLoadingWeights ? (
                                <RefreshCw className="h-6 w-6 animate-spin text-gray-400" />
                              ) : folderWeights ? (
                                `${folderWeights.original.weight.toFixed(1)} MB`
                              ) : (
                                'Chargement...'
                              )}
                            </p>
                            {folderWeights && (
                              <p className="text-xs text-gray-500">
                                {folderWeights.original.count} fichiers
                              </p>
                            )}
                          </div>
                        </div>
                        <button
                          onClick={loadFolderWeights}
                          disabled={isLoadingWeights}
                          className="p-2 text-gray-400 hover:text-gray-600 transition-colors"
                          title="Actualiser les poids"
                        >
                          <RefreshCw className={`h-4 w-4 ${isLoadingWeights ? 'animate-spin' : ''}`} />
                        </button>
                      </div>
                    </div>
                    
                    <div className="bg-white border border-gray-200 rounded-lg p-6">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center">
                          <div className="p-2 bg-green-100 rounded-lg">
                            <Database className="h-6 w-6 text-green-600" />
                          </div>
                          <div className="ml-4">
                            <p className="text-sm font-medium text-gray-600">Poids optimisé</p>
                            <p className="text-2xl font-semibold text-gray-900">
                              {isLoadingWeights ? (
                                <RefreshCw className="h-6 w-6 animate-spin text-gray-400" />
                              ) : folderWeights ? (
                                `${folderWeights.optimized.weight.toFixed(1)} MB`
                              ) : (
                                'Chargement...'
                              )}
                            </p>
                            {folderWeights && (
                              <p className="text-xs text-green-600">
                                {folderWeights.savings > 0 ? 
                                  `-${folderWeights.savings.toFixed(1)}% économisé` : 
                                  'Aucune économie'
                                }
                              </p>
                            )}
                            {folderWeights && (
                              <p className="text-xs text-gray-500">
                                {folderWeights.optimized.count} fichiers WebP
                              </p>
                            )}
                          </div>
                        </div>
                        <button
                          onClick={loadFolderWeights}
                          disabled={isLoadingWeights}
                          className="p-2 text-gray-400 hover:text-gray-600 transition-colors"
                          title="Actualiser les poids"
                        >
                          <RefreshCw className={`h-4 w-4 ${isLoadingWeights ? 'animate-spin' : ''}`} />
                        </button>
                      </div>
                    </div>
                  </div>

                  {/* Paramètres d'optimisation */}
                  <div className="bg-white border border-gray-200 rounded-lg p-6">
                    <div className="flex items-center justify-between mb-6">
                      <div>
                        <h3 className="text-lg font-semibold text-gray-900">Paramètres d'optimisation</h3>
                        <p className="text-sm text-gray-600 mt-1">Configurez les paramètres de compression et de redimensionnement</p>
                      </div>
                      <div className="flex space-x-3">
                        <button
                          onClick={() => {
                            setWebpQuality(85)
                            setEnableResize(true)
                            setMaxWidth(1920)
                            setMaxHeight(1080)
                            setJpegQuality(80)
                            setOptimizationLogs([{
                              type: 'info',
                              message: '🔄 Paramètres réinitialisés aux valeurs par défaut.',
                              timestamp: new Date().toLocaleTimeString()
                            }])
                          }}
                          className="px-4 py-2 text-sm border border-gray-300 rounded-md hover:bg-gray-50 transition-colors"
                        >
                          Réinitialiser
                        </button>
                        <button
                          onClick={saveOptimizationSettings}
                          disabled={isSavingSettings}
                          className={`px-4 py-2 text-sm rounded-md transition-colors flex items-center ${
                            isSavingSettings 
                              ? 'bg-gray-400 text-white cursor-not-allowed' 
                              : settingsSaved
                                ? 'bg-green-500 text-white'
                                : 'bg-orange-500 text-white hover:bg-orange-600'
                          }`}
                        >
                          {isSavingSettings ? (
                            <>
                              <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                              Sauvegarde...
                            </>
                          ) : settingsSaved ? (
                            <>
                              <CheckCircle className="h-4 w-4 mr-2" />
                              Sauvegardé !
                            </>
                          ) : (
                            <>
                              <Save className="h-4 w-4 mr-2" />
                              Sauvegarder
                            </>
                          )}
                        </button>
                      </div>
                    </div>

                    <div className="space-y-6">
                      {/* Qualité WebP */}
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Qualité WebP
                        </label>
                        <div className="space-y-2">
                          <input
                            type="range"
                            min="50"
                            max="100"
                            value={webpQuality}
                            onChange={(e) => setWebpQuality(parseInt(e.target.value))}
                            className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer slider"
                          />
                          <div className="flex justify-between text-xs text-gray-500">
                            <span>50%</span>
                            <span className="font-medium text-orange-600">{webpQuality}%</span>
                            <span>100%</span>
                          </div>
                        </div>
                        <p className="text-xs text-gray-500 mt-1">
                          Qualité de compression WebP (plus élevé = meilleure qualité, plus lourd)
                        </p>
                      </div>

                      {/* Dimensions */}
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        {/* Largeur maximale */}
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            Largeur maximale (px)
                          </label>
                          <input
                            type="number"
                            value={maxWidth}
                            onChange={(e) => setMaxWidth(parseInt(e.target.value))}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-orange-500 focus:border-orange-500"
                          />
                        </div>

                        {/* Hauteur maximale */}
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            Hauteur maximale (px)
                          </label>
                          <input
                            type="number"
                            value={maxHeight}
                            onChange={(e) => setMaxHeight(parseInt(e.target.value))}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-orange-500 focus:border-orange-500"
                          />
                        </div>
                      </div>

                    </div>
                  </div>

                  {/* Actions d'optimisation */}
                  <div className="bg-white border border-gray-200 rounded-lg p-6">
                    <div className="flex items-center justify-between mb-6">
                      <div>
                        <h3 className="text-lg font-semibold text-gray-900">Optimisation des images</h3>
                        <p className="text-sm text-gray-600 mt-1">Lancez l'optimisation des images avec les paramètres configurés</p>
                      </div>
                      <button
                        onClick={isOptimizing ? stopOptimization : startOptimization}
                        disabled={isOptimizing}
                        className={`px-6 py-3 rounded-lg font-medium transition-colors flex items-center ${
                          isOptimizing
                            ? 'bg-red-500 text-white hover:bg-red-600'
                            : 'bg-orange-500 text-white hover:bg-orange-600'
                        }`}
                      >
                        {isOptimizing ? (
                          <>
                            <RefreshCw className="h-5 w-5 mr-2 animate-spin" />
                            Arrêter l'optimisation
                          </>
                        ) : (
                          <>
                            <Upload className="h-5 w-5 mr-2" />
                            Lancer l'optimisation
                          </>
                        )}
                      </button>
                    </div>

                    {/* Barre de progression */}
                    {isOptimizing && (
                      <div className="mb-6">
                        <div className="flex justify-between text-sm text-gray-600 mb-2">
                          <span>Progression</span>
                          <span>{optimizationProgress}%</span>
                        </div>
                        <div className="w-full bg-gray-200 rounded-full h-2">
                          <div
                            className="bg-orange-500 h-2 rounded-full transition-all duration-300"
                            style={{ width: `${optimizationProgress}%` }}
                          ></div>
                        </div>
                      </div>
                    )}

                    {/* Logs d'optimisation */}
                    <div className="bg-white border border-gray-200 rounded-lg p-4 max-h-64 overflow-y-auto">
                      <div className="text-sm text-gray-700 space-y-1">
                        {optimizationLogs.length === 0 ? (
                          <div className="text-gray-500 italic">
                            {isOptimizing ? 'En cours...' : 'Aucun log disponible. Lancez une optimisation pour voir les détails.'}
                          </div>
                        ) : (
                          optimizationLogs.map((log, index) => (
                            <div key={index} className={`flex items-start space-x-2 ${
                              log.type === 'error' ? 'text-red-600' : 
                              log.type === 'success' ? 'text-green-600' : 
                              'text-gray-700'
                            }`}>
                              <span className="text-gray-500 text-xs mt-0.5">[{log.timestamp}]</span>
                              <span className="flex-1">{log.message}</span>
                            </div>
                          ))
                        )}
                      </div>
                    </div>
                  </div>

                </div>
              )}

              {/* Gestion des utilisateurs */}
              {activeGeneralTab === 'users' && (
                <div className="space-y-6">
                  {/* En-tête */}
                  <div className="flex justify-between items-center">
                    <div>
                      <h3 className="text-lg font-semibold text-gray-900">Gestion des utilisateurs</h3>
                      <p className="text-sm text-gray-600">Créez et gérez les comptes administrateurs</p>
                    </div>
                    <button
                      onClick={() => {
                        setEditingUser(null)
                        setShowUserForm(true)
                      }}
                      className="btn-primary flex items-center"
                    >
                      <Plus className="h-4 w-4 mr-2" />
                      Nouvel utilisateur
                    </button>
                  </div>

                  {/* Filtres et recherche */}
                  <div className="mb-4 space-y-4">
                    {/* Barre de recherche */}
                    <div className="flex items-center space-x-4">
                      <label className="text-sm font-medium text-gray-700">
                        Rechercher :
                      </label>
                      <input
                        type="text"
                        value={userSearchTerm}
                        onChange={(e) => setUserSearchTerm(e.target.value)}
                        placeholder="Nom, email, prénom..."
                        className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500 text-sm w-64"
                      />
                      {userSearchTerm && (
                        <button
                          onClick={() => setUserSearchTerm('')}
                          className="text-gray-400 hover:text-gray-600"
                        >
                          <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                          </svg>
                        </button>
                      )}
                    </div>
                    
                    {/* Filtre par rôle */}
                    <div className="flex items-center space-x-4">
                      <label className="text-sm font-medium text-gray-700">
                        Filtrer par rôle :
                      </label>
                      <select
                        value={userRoleFilter}
                        onChange={(e) => setUserRoleFilter(e.target.value)}
                        className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500 text-sm"
                      >
                        <option value="all">Tous les utilisateurs</option>
                        <option value="admin">Administrateurs</option>
                        <option value="user">Utilisateurs</option>
                      </select>
                      <span className="text-sm text-gray-500">
                        ({users.filter(user => {
                          const matchesRole = userRoleFilter === 'all' || user.role === userRoleFilter;
                          const matchesSearch = !userSearchTerm || 
                            (user.username && user.username.toLowerCase().includes(userSearchTerm.toLowerCase())) ||
                            (user.email && user.email.toLowerCase().includes(userSearchTerm.toLowerCase())) ||
                            (user.first_name && user.first_name.toLowerCase().includes(userSearchTerm.toLowerCase())) ||
                            (user.last_name && user.last_name.toLowerCase().includes(userSearchTerm.toLowerCase()));
                          return matchesRole && matchesSearch;
                        }).length} utilisateur{users.filter(user => {
                          const matchesRole = userRoleFilter === 'all' || user.role === userRoleFilter;
                          const matchesSearch = !userSearchTerm || 
                            (user.username && user.username.toLowerCase().includes(userSearchTerm.toLowerCase())) ||
                            (user.email && user.email.toLowerCase().includes(userSearchTerm.toLowerCase())) ||
                            (user.first_name && user.first_name.toLowerCase().includes(userSearchTerm.toLowerCase())) ||
                            (user.last_name && user.last_name.toLowerCase().includes(userSearchTerm.toLowerCase()));
                          return matchesRole && matchesSearch;
                        }).length > 1 ? 's' : ''})
                      </span>
                    </div>
                  </div>

                  {/* Liste des utilisateurs */}
                  <div className="bg-white rounded-lg border border-gray-200">
                    {isLoadingUsers ? (
                      <div className="p-8 text-center">
                        <RefreshCw className="h-6 w-6 animate-spin mx-auto text-gray-400" />
                        <p className="text-sm text-gray-500 mt-2">Chargement des utilisateurs...</p>
                      </div>
                    ) : (
                      <div className="overflow-x-auto">
                        <table className="w-full">
                          <thead className="bg-gray-50 border-b border-gray-200">
                            <tr>
                              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                Utilisateur
                              </th>
                              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                Email
                              </th>
                              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                Rôle
                              </th>
                              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                Créé le
                              </th>
                              <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                                Actions
                              </th>
                            </tr>
                          </thead>
                          <tbody className="bg-white divide-y divide-gray-200">
                            {users
                              .filter(user => {
                                const matchesRole = userRoleFilter === 'all' || user.role === userRoleFilter;
                                const matchesSearch = !userSearchTerm || 
                                  (user.username && user.username.toLowerCase().includes(userSearchTerm.toLowerCase())) ||
                                  (user.email && user.email.toLowerCase().includes(userSearchTerm.toLowerCase())) ||
                                  (user.first_name && user.first_name.toLowerCase().includes(userSearchTerm.toLowerCase())) ||
                                  (user.last_name && user.last_name.toLowerCase().includes(userSearchTerm.toLowerCase()));
                                return matchesRole && matchesSearch;
                              })
                              .map((user) => (
                              <tr key={user.id} className="hover:bg-gray-50">
                                <td className="px-6 py-4 whitespace-nowrap">
                                  <div className="flex items-center">
                                    <div className="flex-shrink-0 h-8 w-8">
                                      <div className="h-8 w-8 rounded-full bg-orange-100 flex items-center justify-center">
                                        <User className="h-4 w-4 text-orange-600" />
                                      </div>
                                    </div>
                                    <div className="ml-4">
                                      <div className="text-sm font-medium text-gray-900">
                                        {user.first_name} {user.last_name}
                                      </div>
                                      <div className="text-sm text-gray-500">
                                        @{user.username}
                                      </div>
                                    </div>
                                  </div>
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                  {user.email}
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap">
                                  <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                                    user.role === 'admin' 
                                      ? 'bg-orange-100 text-orange-800' 
                                      : 'bg-gray-100 text-gray-800'
                                  }`}>
                                    {user.role}
                                  </span>
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                  {new Date(user.created_at).toLocaleDateString('fr-FR')}
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                                  <div className="flex items-center justify-end space-x-2">
                                    <button
                                      onClick={() => {
                                        setEditingUser(user)
                                        setShowUserForm(true)
                                      }}
                                      className="text-orange-600 hover:text-orange-900"
                                    >
                                      <Edit className="h-4 w-4" />
                                    </button>
                                    <button
                                      onClick={() => handleDeleteUser(user.id)}
                                      className="text-red-600 hover:text-red-900"
                                    >
                                      <Trash2 className="h-4 w-4" />
                                    </button>
                                  </div>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                        
                        {users.length === 0 && (
                          <div className="p-8 text-center text-gray-500">
                            <Users className="h-12 w-12 mx-auto text-gray-300 mb-4" />
                            <p>Aucun utilisateur trouvé</p>
                            <p className="text-sm">Créez votre premier utilisateur administrateur</p>
                          </div>
                        )}
                      </div>
                    )}
                  </div>

                  {/* Formulaire de création/édition d'utilisateur */}
                  {showUserForm && (
                    <div className="bg-white rounded-lg border border-gray-200 p-6">
                      <h4 className="text-lg font-medium text-gray-900 mb-4">
                        {editingUser ? 'Modifier l\'utilisateur' : 'Nouvel utilisateur'}
                      </h4>
                      
                      <form onSubmit={(e) => {
                        e.preventDefault()
                        const formData = new FormData(e.target)
                        const userData = {
                          username: formData.get('username'),
                          email: formData.get('email'),
                          first_name: formData.get('first_name'),
                          last_name: formData.get('last_name'),
                          role: formData.get('role'),
                          password: formData.get('password')
                        }
                        handleSaveUser(userData)
                      }} className="space-y-4">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                              Nom d'utilisateur *
                            </label>
                            <input
                              type="text"
                              name="username"
                              required
                              defaultValue={editingUser?.username || ''}
                              className="input-field"
                              placeholder="admin"
                            />
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                              Email *
                            </label>
                            <input
                              type="email"
                              name="email"
                              required
                              defaultValue={editingUser?.email || ''}
                              className="input-field"
                              placeholder="admin@example.com"
                            />
                          </div>
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                              Prénom *
                            </label>
                            <input
                              type="text"
                              name="first_name"
                              required
                              defaultValue={editingUser?.first_name || ''}
                              className="input-field"
                              placeholder="Admin"
                            />
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                              Nom *
                            </label>
                            <input
                              type="text"
                              name="last_name"
                              required
                              defaultValue={editingUser?.last_name || ''}
                              className="input-field"
                              placeholder="User"
                            />
                          </div>
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                              Rôle *
                            </label>
                            <select name="role" required className="input-field">
                              <option value="admin" selected={editingUser?.role === 'admin'}>
                                Administrateur
                              </option>
                              <option value="user" selected={editingUser?.role === 'user'}>
                                Utilisateur
                              </option>
                            </select>
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                              {editingUser ? 'Nouveau mot de passe (laisser vide pour ne pas changer)' : 'Mot de passe *'}
                            </label>
                            <input
                              type="password"
                              name="password"
                              required={!editingUser}
                              className="input-field"
                              placeholder="••••••••"
                            />
                          </div>
                        </div>

                        <div className="flex justify-end space-x-3 pt-4">
                          <button
                            type="button"
                            onClick={() => {
                              setShowUserForm(false)
                              setEditingUser(null)
                            }}
                            className="btn-outline"
                          >
                            Annuler
                          </button>
                          <button
                            type="submit"
                            disabled={isSavingUser}
                            className="btn-primary flex items-center"
                          >
                            {isSavingUser ? (
                              <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                            ) : (
                              <Save className="h-4 w-4 mr-2" />
                            )}
                            {isSavingUser ? 'Sauvegarde...' : 'Sauvegarder'}
                          </button>
                        </div>
                      </form>
                    </div>
                  )}
                </div>
              )}

              {/* Rentman Settings */}
              {activeTab === 'rentman' && (
                <div className="space-y-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <h2 className="text-2xl font-semibold text-gray-900">
                        {activeRentmanTab === 'import'
                          ? 'Import produits'
                          : activeRentmanTab === 'import_clients'
                          ? 'Import clients'
                          : activeRentmanTab === 'export'
                          ? 'Export commandes'
                          : activeRentmanTab === 'export_clients'
                          ? 'Export clients'
                          : activeRentmanTab === 'caution'
                          ? 'Calculateur de caution'
                          : activeRentmanTab === 'status'
                          ? 'Statut du Système'
                          : 'Import produits'}
                      </h2>
                      <nav className="flex items-center space-x-2 text-sm text-gray-500 mt-1">
                        <span>Maintenance</span>
                        <span>›</span>
                        <span className="text-orange-600">Rentman</span>
                        <span>›</span>
                        <span className="font-medium">
                          {activeRentmanTab === 'import' && 'Import produits'}
                          {activeRentmanTab === 'import_clients' && 'Import clients'}
                          {activeRentmanTab === 'export' && 'Export commandes'}
                          {activeRentmanTab === 'export_clients' && 'Export clients'}
                          {activeRentmanTab === 'caution' && 'Caution'}
                          {activeRentmanTab === 'status' && 'Statut'}
                        </span>
                      </nav>
                    </div>
                    <div className="text-sm text-gray-500 bg-gray-50 px-3 py-1 rounded-full">
                      {activeRentmanTab === 'import' && 'Import produits'}
                      {activeRentmanTab === 'import_clients' && 'Import clients'}
                      {activeRentmanTab === 'export' && 'Export commandes'}
                      {activeRentmanTab === 'export_clients' && 'Export clients'}
                      {activeRentmanTab === 'caution' && 'Gestion des cautions'}
                      {activeRentmanTab === 'status' && 'Statut système'}
                    </div>
                  </div>


                  {/* Import Settings */}
                  {activeRentmanTab === 'import' && (
                <div className="space-y-6">

                  {/* Import Options */}
                  <div className="border border-gray-200 rounded-lg p-6">
                    <h3 className="text-lg font-semibold text-gray-900 mb-4">
                      Options d'import
                    </h3>
                    
                    <div className="space-y-4">
                      <div className="flex items-center justify-between">
                        <div>
                          <label className="text-sm font-medium text-gray-700">
                            Importer les images
                          </label>
                        </div>
                        <input
                          type="checkbox"
                          checked={settings.import_images}
                          onChange={(e) => handleInputChange('import_images', e.target.checked)}
                          className="h-4 w-4 text-orange-600 focus:ring-orange-500 border-gray-300 rounded"
                        />
                      </div>

                      <div className="flex items-center justify-between">
                        <div>
                          <label className="text-sm font-medium text-gray-700">
                            Importer les fichiers
                          </label>
                        </div>
                        <input
                          type="checkbox"
                          checked={settings.import_files}
                          onChange={(e) => handleInputChange('import_files', e.target.checked)}
                          className="h-4 w-4 text-orange-600 focus:ring-orange-500 border-gray-300 rounded"
                        />
                      </div>

                      <div className="flex items-center justify-between">
                        <div>
                          <label className="text-sm font-medium text-gray-700">
                            Importer les tags
                          </label>
                        </div>
                        <input
                          type="checkbox"
                          checked={settings.import_tags}
                          onChange={(e) => handleInputChange('import_tags', e.target.checked)}
                          className="h-4 w-4 text-orange-600 focus:ring-orange-500 border-gray-300 rounded"
                        />
                      </div>

                      <div className="flex items-center justify-between">
                        <div>
                          <label className="text-sm font-medium text-gray-700">
                            Importer les accessoires comme cross-sells
                          </label>
                        </div>
                        <input
                          type="checkbox"
                          checked={settings.import_accessories}
                          onChange={(e) => handleInputChange('import_accessories', e.target.checked)}
                          className="h-4 w-4 text-orange-600 focus:ring-orange-500 border-gray-300 rounded"
                        />
                      </div>

                      <div className="flex items-center justify-between">
                        <div>
                          <label className="text-sm font-medium text-gray-700">
                            Importer les alternatives comme upsells
                          </label>
                        </div>
                        <input
                          type="checkbox"
                          checked={settings.import_alternatives}
                          onChange={(e) => handleInputChange('import_alternatives', e.target.checked)}
                          className="h-4 w-4 text-orange-600 focus:ring-orange-500 border-gray-300 rounded"
                        />
                      </div>

                      <div className="flex items-center justify-between">
                        <div>
                          <label className="text-sm font-medium text-gray-700">
                            Importer les dossiers comme catégories
                          </label>
                        </div>
                        <input
                          type="checkbox"
                          checked={settings.import_folders_as_categories}
                          onChange={(e) => handleInputChange('import_folders_as_categories', e.target.checked)}
                          className="h-4 w-4 text-orange-600 focus:ring-orange-500 border-gray-300 rounded"
                        />
                      </div>
                    </div>
                  </div>

                  {/* Synchronisation automatique */}
                  <div className="border border-gray-200 rounded-lg p-6">
                    <h3 className="text-lg font-semibold text-gray-900 mb-4">
                      Synchronisation automatique
                    </h3>
                    
                    <div className="space-y-4">
                      <div className="flex items-center justify-between">
                        <div>
                          <label className="text-sm font-medium text-gray-700">
                            Activer la synchronisation automatique
                          </label>
                          <p className="text-sm text-gray-500 mt-1">Permet d'activer/désactiver toutes les synchronisations automatiques</p>
                        </div>
                        <input
                          type="checkbox"
                          checked={settings.auto_sync_enabled}
                          onChange={(e) => handleInputChange('auto_sync_enabled', e.target.checked)}
                          className="h-4 w-4 text-orange-600 focus:ring-orange-500 border-gray-300 rounded"
                        />
                      </div>

                      <div className="flex items-center justify-between">
                        <div>
                          <label className="text-sm font-medium text-gray-700">
                            Synchronisation quotidienne (2h00)
                          </label>
                          <p className="text-sm text-gray-500 mt-1">Synchronisation complète tous les jours à 2h du matin</p>
                        </div>
                        <input
                          type="checkbox"
                          checked={settings.auto_sync_daily}
                          onChange={(e) => handleInputChange('auto_sync_daily', e.target.checked)}
                          disabled={!settings.auto_sync_enabled}
                          className="h-4 w-4 text-orange-600 focus:ring-orange-500 border-gray-300 rounded disabled:opacity-50"
                        />
                      </div>

                      <div className="flex items-center justify-between">
                        <div>
                          <label className="text-sm font-medium text-gray-700">
                            Synchronisation fréquente (toutes les 6h)
                          </label>
                          <p className="text-sm text-gray-500 mt-1">Synchronisation complète toutes les 6h (2h, 8h, 14h, 20h)</p>
                        </div>
                        <input
                          type="checkbox"
                          checked={settings.auto_sync_frequent}
                          onChange={(e) => handleInputChange('auto_sync_frequent', e.target.checked)}
                          disabled={!settings.auto_sync_enabled}
                          className="h-4 w-4 text-orange-600 focus:ring-orange-500 border-gray-300 rounded disabled:opacity-50"
                        />
                      </div>

                      <div className="flex items-center justify-between">
                        <div>
                          <label className="text-sm font-medium text-gray-700">
                            Synchronisation fréquente (toutes les 24h)
                          </label>
                          <p className="text-sm text-gray-500 mt-1">Synchronisation complète toutes les 24h avec envoi d'email de rapport</p>
                        </div>
                        <input
                          type="checkbox"
                          checked={settings.auto_sync_24h}
                          onChange={(e) => handleInputChange('auto_sync_24h', e.target.checked)}
                          disabled={!settings.auto_sync_enabled}
                          className="h-4 w-4 text-orange-600 focus:ring-orange-500 border-gray-300 rounded disabled:opacity-50"
                        />
                      </div>
                    </div>
                  </div>

                  {/* Import Folders */}
                  <div className="border border-gray-200 rounded-lg p-6">
                    <h3 className="text-lg font-semibold text-gray-900 mb-4">
                      Importer les produits depuis
                    </h3>
                    
                    <div className="mb-4">
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Mode d'import
                      </label>
                      <div className="space-y-2">
                        <label className="flex items-center">
                          <input
                            type="radio"
                            name="import_from_folders"
                            value="all"
                            checked={settings.import_from_folders === 'all'}
                            onChange={(e) => handleInputChange('import_from_folders', e.target.value)}
                            className="h-4 w-4 text-orange-600 focus:ring-orange-500 border-gray-300 mr-2"
                          />
                          <span className="text-sm text-gray-700">Tous les dossiers</span>
                        </label>
                        <label className="flex items-center">
                          <input
                            type="radio"
                            name="import_from_folders"
                            value="selected"
                            checked={settings.import_from_folders === 'selected'}
                            onChange={(e) => handleInputChange('import_from_folders', e.target.value)}
                            className="h-4 w-4 text-orange-600 focus:ring-orange-500 border-gray-300 mr-2"
                          />
                          <span className="text-sm text-gray-700">Dossiers sélectionnés uniquement</span>
                        </label>
                      </div>
                      <p className="text-xs text-gray-500 mt-1">
                        {settings.import_from_folders === 'all' 
                          ? 'L\'import automatique synchronisera tous les dossiers disponibles (dossiers sensibles exclus automatiquement)'
                          : 'L\'import automatique ne synchronisera que les dossiers cochés ci-dessous'
                        }
                      </p>
                      <div className="mt-2 p-2 bg-blue-50 border border-blue-200 rounded text-xs text-blue-700">
                        <strong>🛡️ Protection des données sensibles :</strong> Les dossiers "Client", "Transport", "Permanent", "Fournisseur", "Lieux", "Ressources humaines" et "Intermittents" sont automatiquement exclus de tous les imports pour protéger vos données sensibles.
                      </div>
                    </div>

                    {settings.import_from_folders === 'selected' && (
                      <>
                        <div className="mt-4">
                          <label className="flex items-center">
                            <input
                              type="checkbox"
                              checked={settings.import_include_subfolders}
                              onChange={(e) => handleInputChange('import_include_subfolders', e.target.checked)}
                              className="h-4 w-4 text-orange-600 focus:ring-orange-500 border-gray-300 rounded mr-2"
                            />
                            Inclure les sous-dossiers
                          </label>
                        </div>

                        <div className="mt-4">
                          <div className="flex items-center justify-between mb-2">
                            <div className="text-xs text-gray-500">
                              Cochez un dossier pour sélectionner uniquement ce dossier, ou activez l'option ci-dessus pour inclure ses sous-dossiers automatiquement.
                            </div>
                          </div>
                          <div className="max-h-60 overflow-y-auto border border-gray-200 rounded-lg p-3">
                            {(foldersByParent['root'] || []).map((rootFolder) => (
                              renderFolderNode(rootFolder, 0)
                            ))}
                            {(!foldersList || foldersList.length === 0) && (
                              <div className="text-center text-gray-500 py-6 text-sm">Aucun dossier disponible</div>
                            )}
                          </div>
                        </div>
                      </>
                    )}
                  </div>

                  {/* Import Folders - garder uniquement l'arborescence si nécessaire */}


                  {/* Synchronisation */}
                  <div className="border border-gray-200 rounded-lg p-6">
                    <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                      <RefreshCw className="h-5 w-5 mr-2 text-orange-500" />
                      Synchronisation
                    </h3>
                    
                    <div className="space-y-4">
                      <p className="text-sm text-gray-600">
                        Lancez la synchronisation pour importer les produits et catégories selon vos paramètres d'import.
                      </p>
                      
                      {!isAuthenticated ? (
                        <div className="text-sm text-gray-600 bg-yellow-50 p-3 rounded-lg border border-yellow-200">
                          <AlertCircle className="h-4 w-4 inline mr-2" />
                          Vous devez être connecté en admin pour synchroniser.
                          <a href="/admin/login" className="text-orange-600 hover:text-orange-800 ml-1 underline">
                            Se connecter
                          </a>
                        </div>
                      ) : (
                        <div className="flex gap-3">
                          <button
                            onClick={handleSync}
                            disabled={syncMutation.isLoading}
                            className="btn-primary flex items-center"
                          >
                            {syncMutation.isLoading ? (
                              <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                            ) : (
                              <RefreshCw className="h-4 w-4 mr-2" />
                            )}
                            Synchroniser
                          </button>
                          
                          <button
                            onClick={() => stopSyncMutation.mutate()}
                            disabled={stopSyncMutation.isLoading}
                            className="btn-secondary flex items-center bg-red-600 hover:bg-red-700 text-white disabled:bg-gray-400 disabled:cursor-not-allowed"
                          >
                            {stopSyncMutation.isLoading ? (
                              <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                            ) : (
                              <Square className="h-4 w-4 mr-2" />
                            )}
                            Arrêter
                          </button>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
                  )}

                  {/* Export Settings */}
                  {activeRentmanTab === 'export' && (
                <div className="space-y-6">
                  <div className="border border-gray-200 rounded-lg p-6">
                    <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                      <Upload className="h-5 w-5 mr-2 text-orange-500" />
                      Export Commandes
                    </h3>
                    <p className="text-sm text-gray-600 mb-6">
                      L'export des commandes vers Rentman se fait automatiquement lors de la validation des réservations.
                    </p>
                    
                    <div className="space-y-4">
                      <div className="bg-orange-50 border border-orange-200 rounded-lg p-4">
                        <div className="flex items-center justify-between">
                          <div>
                            <label className="text-sm font-medium text-gray-700">
                              Envoi automatique vers Rentman
                            </label>
                            <p className="text-xs text-gray-500 mt-1">
                              Créer automatiquement un projet Rentman lors de la validation d'une réservation
                            </p>
                          </div>
                          <input
                            type="checkbox"
                            checked={settings.auto_export_to_rentman}
                            onChange={(e) => handleInputChange('auto_export_to_rentman', e.target.checked)}
                            className="h-4 w-4 text-orange-600 focus:ring-orange-500 border-gray-300 rounded"
                          />
                        </div>
                      </div>
                    </div>
                    
                    {/* Bouton de sauvegarde */}
                    <div className="mt-6 pt-4 border-t border-gray-200">
                      <button
                        onClick={async () => {
                          try {
                            await apiService.post('/rentman/settings', settings)
                            toast.success('Paramètres d\'export sauvegardés avec succès')
                          } catch (e) {
                            toast.error(e.response?.data?.error || 'Erreur lors de la sauvegarde')
                          }
                        }}
                        className="btn-primary flex items-center"
                      >
                        <Save className="h-4 w-4 mr-2" />
                        💾 Sauvegarder les paramètres d'export
                      </button>
                    </div>
                  </div>
                  
                  {/* Synchroniser le statut d'une réservation */}
                  <div className="border border-gray-200 rounded-lg p-6">
                    <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                      <RefreshCw className="h-5 w-5 mr-2 text-blue-500" />
                      Synchroniser le statut d'une réservation
                    </h3>
                    <p className="text-sm text-gray-600 mb-6">
                      Récupérer le statut d'une demande Rentman et mettre à jour la réservation correspondante.
                    </p>
                    
                    <form onSubmit={async (e) => {
                      e.preventDefault()
                      const formData = new FormData(e.target)
                      const reservationId = formData.get('reservationId')
                      const rentmanProjectRequestId = formData.get('rentmanProjectRequestId')
                      
                      if (!reservationId || !rentmanProjectRequestId) {
                        toast.error('Veuillez entrer les deux ID')
                        return
                      }
                      
                      try {
                        const response = await apiService.post('/rentman/sync-reservation-status', {
                          reservationId,
                          rentmanProjectRequestId
                        })
                        toast.success(`Statut synchronisé: ${response.data.data.rentmanStatus}`)
                        e.target.reset()
                      } catch (error) {
                        toast.error(error.response?.data?.message || 'Erreur lors de la synchronisation')
                      }
                    }} className="space-y-4">
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                          <label htmlFor="reservationId" className="block text-sm font-medium text-gray-700 mb-2">
                            ID de la réservation
                          </label>
                          <input
                            type="text"
                            id="reservationId"
                            name="reservationId"
                            placeholder="Ex: 123"
                            className="w-full rounded-lg border-gray-300 shadow-sm focus:border-orange-500 focus:ring-orange-500"
                            required
                          />
                        </div>
                        <div>
                          <label htmlFor="rentmanProjectRequestId" className="block text-sm font-medium text-gray-700 mb-2">
                            ID de la demande Rentman
                          </label>
                          <input
                            type="text"
                            id="rentmanProjectRequestId"
                            name="rentmanProjectRequestId"
                            placeholder="Ex: 45678"
                            className="w-full rounded-lg border-gray-300 shadow-sm focus:border-orange-500 focus:ring-orange-500"
                            required
                          />
                        </div>
                      </div>
                      <button
                        type="submit"
                        className="btn-primary flex items-center"
                      >
                        <RefreshCw className="h-4 w-4 mr-2" />
                        Synchroniser le statut
                      </button>
                      <p className="text-xs text-gray-500">
                        ℹ️ Les statuts Rentman: accepted → confirmé, declined/cancelled → annulé, pending → en attente
                      </p>
                    </form>
                  </div>
                  
                  {/* Supprimer un projet exporté */}
                  <div className="border border-gray-200 rounded-lg p-6">
                    <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                      <Trash2 className="h-5 w-5 mr-2 text-red-500" />
                      Supprimer un projet Rentman
                    </h3>
                    <p className="text-sm text-gray-600 mb-6">
                      Supprimer un projet exporté vers Rentman en utilisant son ID.
                    </p>
                    
                    <form onSubmit={async (e) => {
                      e.preventDefault()
                      const formData = new FormData(e.target)
                      const projectId = formData.get('projectId')
                      
                      if (!projectId) {
                        toast.error('Veuillez entrer un ID de projet')
                        return
                      }
                      
                      await handleDeleteProject(projectId)
                      e.target.reset()
                    }} className="space-y-4">
                      <div>
                        <label htmlFor="deleteProjectId" className="block text-sm font-medium text-gray-700 mb-2">
                          ID du projet Rentman
                        </label>
                        <div className="flex gap-3">
                          <input
                            type="text"
                            id="deleteProjectId"
                            name="projectId"
                            placeholder="Ex: 12345"
                            className="flex-1 rounded-lg border-gray-300 shadow-sm focus:border-orange-500 focus:ring-orange-500"
                            required
                          />
                          <button
                            type="submit"
                            disabled={deletingProjectId !== null}
                            className="btn-danger flex items-center whitespace-nowrap"
                          >
                            {deletingProjectId !== null ? (
                              <>
                                <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                                Suppression...
                              </>
                            ) : (
                              <>
                                <Trash2 className="h-4 w-4 mr-2" />
                                Supprimer
                              </>
                            )}
                          </button>
                        </div>
                        <p className="mt-2 text-xs text-gray-500">
                          ⚠️ Cette action supprimera définitivement le projet de Rentman
                        </p>
                      </div>
                    </form>
                  </div>
                </div>
                  )}

                  {/* Export Clients */}
                  {activeRentmanTab === 'export_clients' && (
                <div className="space-y-6">
                  <div className="border border-gray-200 rounded-lg p-6">
                    <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                      <User className="h-5 w-5 mr-2 text-orange-500" />
                      Créer un client dans Rentman
                    </h3>
                    <p className="text-sm text-gray-600 mb-6">
                      Créer un nouveau client directement dans Rentman avec toutes ses informations.
                    </p>
                    
                    <form onSubmit={async (e) => {
                      e.preventDefault()
                      try {
                        const formData = new FormData(e.target)
                        const clientData = {
                          firstname: formData.get('firstname'),
                          surname: formData.get('surname'),
                          name: formData.get('name'),
                          type: formData.get('type'),
                          company_name: formData.get('company_name'),
                          email_1: formData.get('email_1'),
                          phone_1: formData.get('phone_1'),
                          mailing_city: formData.get('mailing_city'),
                          mailing_street: formData.get('mailing_street'),
                          mailing_number: formData.get('mailing_number'),
                          mailing_postalcode: formData.get('mailing_postalcode'),
                          mailing_country: formData.get('mailing_country'),
                          country: formData.get('country'),
                          website: formData.get('website'),
                          vat_code: formData.get('vat_code')
                        }
                        
                        const res = await apiService.post('/rentman/create-contact', clientData)
                        toast.success(`Client créé avec succès dans Rentman (ID: ${res.data.id})`)
                        e.target.reset()
                      } catch (err) {
                        toast.error(err.response?.data?.message || 'Erreur lors de la création du client')
                      }
                    }} className="space-y-4">
                      
                      {/* Informations de base */}
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            Prénom *
                          </label>
                          <input
                            type="text"
                            name="firstname"
                            required
                            className="input-field"
                            placeholder="Jean"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            Nom *
                          </label>
                          <input
                            type="text"
                            name="surname"
                            required
                            className="input-field"
                            placeholder="Dupont"
                          />
                        </div>
                      </div>

                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            Nom complet *
                          </label>
                          <input
                            type="text"
                            name="name"
                            required
                            className="input-field"
                            placeholder="Jean Dupont"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            Type *
                          </label>
                          <select name="type" required className="input-field">
                            <option value="private">Particulier</option>
                            <option value="company">Société</option>
                          </select>
                        </div>
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Nom de l'entreprise
                        </label>
                        <input
                          type="text"
                          name="company_name"
                          className="input-field"
                          placeholder="Société ABC (optionnel)"
                        />
                        <p className="text-xs text-gray-500 mt-1">
                          À remplir uniquement si le type est "Société"
                        </p>
                      </div>

                      {/* Contact */}
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            Email *
                          </label>
                          <input
                            type="email"
                            name="email_1"
                            required
                            className="input-field"
                            placeholder="jean.dupont@example.com"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            Téléphone
                          </label>
                          <input
                            type="tel"
                            name="phone_1"
                            className="input-field"
                            placeholder="0123456789"
                          />
                        </div>
                      </div>

                      {/* Adresse */}
                      <div className="border-t pt-4">
                        <h4 className="text-md font-medium text-gray-900 mb-4">Adresse</h4>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          <div className="md:col-span-2">
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                              Rue
                            </label>
                            <input
                              type="text"
                              name="mailing_street"
                              className="input-field"
                              placeholder="123 Rue de la Paix"
                            />
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                              Numéro
                            </label>
                            <input
                              type="text"
                              name="mailing_number"
                              className="input-field"
                              placeholder="123"
                            />
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                              Code postal
                            </label>
                            <input
                              type="text"
                              name="mailing_postalcode"
                              className="input-field"
                              placeholder="75001"
                            />
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                              Ville
                            </label>
                            <input
                              type="text"
                              name="mailing_city"
                              className="input-field"
                              placeholder="Paris"
                            />
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                              Pays
                            </label>
                            <select name="mailing_country" className="input-field">
                              <option value="fr">France</option>
                              <option value="gp">Guadeloupe</option>
                              <option value="mq">Martinique</option>
                              <option value="gf">Guyane</option>
                              <option value="re">Réunion</option>
                              <option value="yt">Mayotte</option>
                            </select>
                          </div>
                        </div>
                      </div>

                      {/* Informations complémentaires */}
                      <div className="border-t pt-4">
                        <h4 className="text-md font-medium text-gray-900 mb-4">Informations complémentaires</h4>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                              Site web
                            </label>
                            <input
                              type="url"
                              name="website"
                              className="input-field"
                              placeholder="https://www.example.com"
                            />
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                              Code TVA
                            </label>
                            <input
                              type="text"
                              name="vat_code"
                              className="input-field"
                              placeholder="FR12345678901"
                            />
                          </div>
                        </div>
                      </div>

                      <div className="flex justify-end pt-4">
                        <button
                          type="submit"
                          className="btn-primary"
                        >
                          <User className="h-4 w-4 mr-2" />
                          Créer le client dans Rentman
                        </button>
                      </div>
                    </form>
                  </div>
                </div>
                  )}

                  {/* Status Tab */}
                  {activeRentmanTab === 'status' && (
                <div className="space-y-6">

                  {/* Connection Status */}
                  <div className="border border-gray-200 rounded-lg p-6">
                    <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                      <Database className="h-5 w-5 mr-2 text-orange-500" />
                      Statut de Connexion
                    </h3>
                    
                    <div className="flex items-center space-x-3">
                      <div className="flex items-center">
                        <CheckCircle className="h-5 w-5 text-green-500 mr-2" />
                        <span className="text-sm font-medium text-gray-700">
                          Base de données connectée
                        </span>
                      </div>
                    </div>
                  </div>

                  {/* Sync Status */}
                  <div className="border border-gray-200 rounded-lg p-6">
                    <h3 className="text-lg font-semibold text-gray-900 mb-4">
                      Statut de Synchronisation
                    </h3>
                    
                    <div className="space-y-4">
                      <div className="flex items-center justify-between">
                        <span className="text-sm font-medium text-gray-700">
                          Dernière synchronisation
                        </span>
                        <span className="text-sm text-gray-500">
                          {new Date().toLocaleString('fr-FR')}
                        </span>
                      </div>
                      
                      <div className="flex items-center justify-between">
                        <span className="text-sm font-medium text-gray-700">
                          Prochaines synchronisations
                        </span>
                        <span className="text-sm text-gray-500">
                          Quotidienne à 2h00, Toutes les 6h
                        </span>
                      </div>
                    </div>
                  </div>

                  {/* Statistics */}
                  <div className="border border-gray-200 rounded-lg p-6">
                    <h3 className="text-lg font-semibold text-gray-900 mb-4">
                      Statistiques
                    </h3>
                    
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      <div className="text-center p-4 bg-gray-50 rounded-lg">
                        <div className="text-2xl font-bold text-orange-600">0</div>
                        <div className="text-sm text-gray-600">Produits</div>
                      </div>
                      
                      <div className="text-center p-4 bg-gray-50 rounded-lg">
                        <div className="text-2xl font-bold text-orange-600">0</div>
                        <div className="text-sm text-gray-600">Catégories</div>
                      </div>
                      
                      <div className="text-center p-4 bg-gray-50 rounded-lg">
                        <div className="text-2xl font-bold text-orange-600">0</div>
                        <div className="text-sm text-gray-600">Réservations</div>
                      </div>
                    </div>
                  </div>

                  {/* Journal de synchronisation */}
                  <div className="border border-gray-200 rounded-lg p-6">
                    <div className="flex items-center justify-between mb-4">
                      <h3 className="text-lg font-semibold text-gray-900 flex items-center">
                        <Activity className="h-5 w-5 mr-2 text-orange-500" />
                        Journal de synchronisation
                      </h3>
                      <div className="flex items-center space-x-3">
                        <select
                          value={logOperationFilter}
                          onChange={(e) => setLogOperationFilter(e.target.value)}
                          className="input-field"
                        >
                          <option value="all">Toutes opérations</option>
                          <option value="full_sync">Synchronisation complète</option>
                          <option value="categories_sync">Catégories</option>
                          <option value="products_sync">Produits</option>
                        </select>
                        <button
                          onClick={() => queryClient.invalidateQueries('rentman-logs')}
                          className="btn-outline flex items-center"
                        >
                          <RefreshCw className="h-4 w-4 mr-2" />
                          Actualiser
                        </button>
                        <button
                          onClick={handleClearLogs}
                          disabled={clearLogsMutation.isLoading}
                          className="btn-outline flex items-center text-red-600 border-red-300 hover:bg-red-50"
                        >
                          {clearLogsMutation.isLoading ? (
                            <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                          ) : (
                            <Trash2 className="h-4 w-4 mr-2" />
                          )}
                          Vider
                        </button>
                      </div>
                    </div>

                    <div className="max-h-80 overflow-y-auto border border-gray-200 rounded-lg">
                      <table className="w-full text-sm">
                        <thead className="bg-gray-50">
                          <tr>
                            <th className="text-left py-2 px-3 text-gray-700">Heure</th>
                            <th className="text-left py-2 px-3 text-gray-700">Opération</th>
                            <th className="text-left py-2 px-3 text-gray-700">Statut</th>
                            <th className="text-left py-2 px-3 text-gray-700">Message</th>
                          </tr>
                        </thead>
                        <tbody>
                          {(logsData?.logs || []).map((log) => (
                            <tr key={log.id} className="border-b border-gray-100">
                              <td className="py-2 px-3 text-gray-600">{new Date(log.created_at).toLocaleString('fr-FR')}</td>
                              <td className="py-2 px-3 font-medium">{log.operation}</td>
                              <td className="py-2 px-3">
                                <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                                  log.status === 'completed' ? 'text-green-700 bg-green-100' :
                                  log.status === 'started' ? 'text-blue-700 bg-blue-100' :
                                  'text-red-700 bg-red-100'
                                }`}>
                                  {log.status}
                                </span>
                              </td>
                              <td className="py-2 px-3 text-gray-700">{log.message}</td>
                            </tr>
                          ))}
                          {(!logsData?.logs || logsData.logs.length === 0) && (
                            <tr>
                              <td colSpan="4" className="py-6 text-center text-gray-500">
                                Aucun log disponible pour le moment
                              </td>
                            </tr>
                          )}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>
                  )}

                  {/* Caution Settings */}
                  {activeRentmanTab === 'caution' && (
                    <div className="space-y-6">
                      {/* Recherche de projet Rentman */}
                      <div className="border border-gray-200 rounded-lg p-6">
                        <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                          <Shield className="h-5 w-5 mr-2 text-orange-500" />
                          Recherche de Projet Rentman
                        </h3>
                        
                        <div className="space-y-4">
                          {!isAuthenticated && (
                            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                              <div className="flex items-center">
                                <AlertCircle className="h-5 w-5 text-yellow-500 mr-2" />
                                <span className="text-sm text-yellow-700">
                                  Vous devez être connecté en tant qu'administrateur pour rechercher des projets.
                                </span>
                              </div>
                            </div>
                          )}
                          
                          <div className="flex items-center space-x-4">
                            <div className="w-48">
                              <label className="block text-sm font-medium text-gray-700 mb-2">
                                Type de recherche
                              </label>
                              <select
                                value={searchType}
                                onChange={(e) => setSearchType(e.target.value)}
                                className="input-field"
                                disabled={!isAuthenticated}
                              >
                                <option value="id">ID du projet</option>
                                <option value="number">Numéro du projet</option>
                              </select>
                            </div>
                            <div className="flex-1">
                              <label className="block text-sm font-medium text-gray-700 mb-2">
                                {searchType === 'id' ? 'ID du Projet' : 'Numéro du Projet'}
                              </label>
                              <input
                                type="text"
                                value={projectId}
                                onChange={(e) => setProjectId(e.target.value)}
                                placeholder={searchType === 'id' ? 'Entrez l\'ID du projet Rentman' : 'Entrez le numéro du projet Rentman'}
                                className="input-field"
                                disabled={!isAuthenticated}
                                onKeyPress={(e) => {
                                  if (e.key === 'Enter') {
                                    fetchRentmanProject(projectId)
                                  }
                                }}
                              />
                            </div>
                            <div className="pt-6">
                              <button
                                onClick={() => fetchRentmanProject(projectId)}
                                disabled={isLoadingProject || !projectId.trim() || !isAuthenticated}
                                className="btn-primary flex items-center"
                              >
                                {isLoadingProject ? (
                                  <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                                ) : (
                                  <Search className="h-4 w-4 mr-2" />
                                )}
                                {isLoadingProject ? 'Recherche...' : 'Rechercher'}
                              </button>
                            </div>
                          </div>

                          {projectError && (
                            <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                              <div className="flex items-center">
                                <XCircle className="h-5 w-5 text-red-500 mr-2" />
                                <span className="text-sm text-red-700">{projectError}</span>
                              </div>
                            </div>
                          )}
                        </div>
                      </div>

                      {/* Affichage des données du projet */}
                      {projectData && (
                        <div className="border border-gray-200 rounded-lg p-6">
                          <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                            <CheckCircle className="h-5 w-5 mr-2 text-green-500" />
                            Informations du Projet
                          </h3>
                          
                          {/* Total prix à neuf et caution préconisée */}
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
                            {/* Prix total à neuf du matériel */}
                            <div className="bg-gradient-to-r from-orange-50 to-orange-100 border border-orange-200 rounded-lg p-4">
                              <div className="flex items-center justify-between">
                                <div>
                                  <h4 className="text-sm font-medium text-orange-800 mb-1">Prix total à neuf du matériel</h4>
                                  <p className="text-2xl font-bold text-orange-900">
                                    {(() => {
                                      // Total basé sur quantité × prix à neuf unitaire
                                      const total = projectData.items.reduce((sum, it) => {
                                        const unitNew = parseFloat(it.equipment_price_new ?? it.total_new_price);
                                        const qty = parseFloat(it.quantity ?? it.quantity_total ?? it.amount ?? it.count ?? 1);
                                        if (isNaN(unitNew) || unitNew <= 0) return sum;
                                        const q = isNaN(qty) ? 1 : qty;
                                        return sum + unitNew * q;
                                      }, 0);
                                      if (total > 0) return `${total.toFixed(2)} €`;
                                      // Fallback: totaux de groupes uniques s'il n'y a pas d'unitaires
                                      const seen = new Set();
                                      let groupSum = 0;
                                      for (const it of projectData.items) {
                                        if (it.equipment_group_id && !seen.has(it.equipment_group_id)) {
                                          const gv = parseFloat(it.group_total_new_price);
                                          if (!isNaN(gv) && gv > 0) groupSum += gv;
                                          seen.add(it.equipment_group_id);
                                        }
                                      }
                                      return groupSum > 0 ? `${groupSum.toFixed(2)} €` : 'N/A';
                                    })()}
                                  </p>
                                </div>
                                <div className="text-orange-600">
                                  <svg className="w-8 h-8" fill="currentColor" viewBox="0 0 20 20">
                                    <path fillRule="evenodd" d="M3 4a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm0 4a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm0 4a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm0 4a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1z" clipRule="evenodd" />
                                  </svg>
                                </div>
                              </div>
                            </div>

                            {/* Montant caution (15%) */}
                            <div className="bg-gradient-to-r from-blue-50 to-blue-100 border border-blue-200 rounded-lg p-4">
                              <div className="flex items-center justify-between">
                                <div>
                                  <h4 className="text-sm font-medium text-blue-800 mb-1">Montant caution (15%)</h4>
                                  <p className="text-2xl font-bold text-blue-900">
                                    {(() => {
                                      // Calculer le total prix à neuf
                                      const total = projectData.items.reduce((sum, it) => {
                                        const unitNew = parseFloat(it.equipment_price_new ?? it.total_new_price);
                                        const qty = parseFloat(it.quantity ?? it.quantity_total ?? it.amount ?? it.count ?? 1);
                                        if (isNaN(unitNew) || unitNew <= 0) return sum;
                                        const q = isNaN(qty) ? 1 : qty;
                                        return sum + unitNew * q;
                                      }, 0);
                                      
                                      // Fallback: totaux de groupes uniques
                                      if (total <= 0) {
                                        const seen = new Set();
                                        let groupSum = 0;
                                        for (const it of projectData.items) {
                                          if (it.equipment_group_id && !seen.has(it.equipment_group_id)) {
                                            const gv = parseFloat(it.group_total_new_price);
                                            if (!isNaN(gv) && gv > 0) groupSum += gv;
                                            seen.add(it.equipment_group_id);
                                          }
                                        }
                                        if (groupSum > 0) {
                                          const assuranceAmount = groupSum * 0.15;
                                          return `${assuranceAmount.toFixed(2)} €`;
                                        }
                                        return 'N/A';
                                      }
                                      
                                      const assuranceAmount = total * 0.15;
                                      return `${assuranceAmount.toFixed(2)} €`;
                                    })()}
                                  </p>
                                  {(() => {
                                    // Calculer le total prix à neuf pour vérifier si < 300€
                                    const total = projectData.items.reduce((sum, it) => {
                                      const unitNew = parseFloat(it.equipment_price_new ?? it.total_new_price);
                                      const qty = parseFloat(it.quantity ?? it.quantity_total ?? it.amount ?? it.count ?? 1);
                                      if (isNaN(unitNew) || unitNew <= 0) return sum;
                                      const q = isNaN(qty) ? 1 : qty;
                                      return sum + unitNew * q;
                                    }, 0);
                                    
                                    // Fallback: totaux de groupes uniques
                                    let finalTotal = total;
                                    if (total <= 0) {
                                      const seen = new Set();
                                      let groupSum = 0;
                                      for (const it of projectData.items) {
                                        if (it.equipment_group_id && !seen.has(it.equipment_group_id)) {
                                          const gv = parseFloat(it.group_total_new_price);
                                          if (!isNaN(gv) && gv > 0) groupSum += gv;
                                          seen.add(it.equipment_group_id);
                                        }
                                      }
                                      finalTotal = groupSum;
                                    }
                                    
                                    const assuranceAmount = finalTotal * 0.15;
                                    if (assuranceAmount < 300 && finalTotal > 0) {
                                      return (
                                        <p className="text-sm text-blue-600 mt-1">
                                          Minimum: 300,00 €
                                        </p>
                                      );
                                    }
                                    return null;
                                  })()}
                                </div>
                                <div className="text-blue-600">
                                  <svg className="w-8 h-8" fill="currentColor" viewBox="0 0 20 20">
                                    <path fillRule="evenodd" d="M4 2a1 1 0 011 1v2.101a7.002 7.002 0 0111.601 2.566 1 1 0 11-1.885.666A5.002 5.002 0 005.999 7H9a1 1 0 010 2H4a1 1 0 01-1-1V3a1 1 0 011-1zm.008 9.057a1 1 0 011.276.61A5.002 5.002 0 0014.001 13H11a1 1 0 110-2h5a1 1 0 011 1v5a1 1 0 11-2 0v-2.101a7.002 7.002 0 01-11.601-2.566 1 1 0 01.61-1.276z" clipRule="evenodd" />
                                  </svg>
                                </div>
                              </div>
                            </div>
                          </div>
                          
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                            {/* Informations générales */}
                            <div className="space-y-4">
                              <h4 className="font-medium text-gray-900 border-b border-gray-200 pb-2">
                                Informations Générales
                              </h4>
                              
                              <div className="space-y-3">
                                <div>
                                  <label className="text-sm font-medium text-gray-500">ID</label>
                                  <p className="text-sm text-gray-900">{projectData.project?.id || projectData.id || 'N/A'}</p>
                                </div>
                                
                                <div>
                                  <label className="text-sm font-medium text-gray-500">Nom</label>
                                  <p className="text-sm text-gray-900">{projectData.project?.name || projectData.name || 'N/A'}</p>
                                </div>
                                
                                <div>
                                  <label className="text-sm font-medium text-gray-500">Référence</label>
                                  <p className="text-sm text-gray-900">{projectData.project?.reference || projectData.reference || 'N/A'}</p>
                                </div>
                                
                                <div>
                                  <label className="text-sm font-medium text-gray-500">Numéro</label>
                                  <p className="text-sm text-gray-900">{projectData.project?.number || projectData.number || 'N/A'}</p>
                                </div>
                                
                                <div>
                                  <label className="text-sm font-medium text-gray-500">Nom d'affichage</label>
                                  <p className="text-sm text-gray-900">{projectData.project?.displayname || projectData.displayname || 'N/A'}</p>
                                </div>
                              </div>
                            </div>

                            {/* Informations financières */}
                            <div className="space-y-4">
                              <h4 className="font-medium text-gray-900 border-b border-gray-200 pb-2">
                                Informations Financières
                              </h4>
                              
                              <div className="space-y-3">
                                <div>
                                  <label className="text-sm font-medium text-gray-500">Caution remboursable</label>
                                  <p className="text-sm text-gray-900 font-medium">
                                    {projectData.project?.refundabledeposit || projectData.refundabledeposit ? `${projectData.project?.refundabledeposit || projectData.refundabledeposit} €` : 'N/A'}
                                  </p>
                                </div>
                                
                                <div>
                                  <label className="text-sm font-medium text-gray-500">Statut de la caution</label>
                                  <p className="text-sm text-gray-900">
                                    {(() => {
                                      const status = projectData.project?.deposit_status || projectData.deposit_status;
                                      if (!status) return 'N/A';
                                      
                                      // Traduction et formatage du statut
                                      const statusMap = {
                                        'no_deposit_paid': 'Aucun dépôt payé',
                                        'deposit_paid': 'Dépôt payé',
                                        'partial_deposit': 'Dépôt partiel',
                                        'full_deposit': 'Dépôt complet'
                                      };
                                      
                                      return statusMap[status] || status;
                                    })()}
                                  </p>
                                </div>
                                
                                
                                
                                
                                
                                <div>
                                  <label className="text-sm font-medium text-gray-500">Déjà facturé</label>
                                  <p className="text-sm text-gray-900">
                                    {projectData.project?.already_invoiced || projectData.already_invoiced ? `${projectData.project?.already_invoiced || projectData.already_invoiced} €` : 'N/A'}
                                  </p>
                                </div>
                                
                                <div>
                                  <label className="text-sm font-medium text-gray-500">Coûts d'achat</label>
                                  <p className="text-sm text-gray-900 font-medium">
                                    {projectData.project?.purchasecosts || projectData.purchasecosts ? `${projectData.project?.purchasecosts || projectData.purchasecosts} €` : 'N/A'}
                                  </p>
                                </div>
                              </div>
                            </div>
                          </div>

                          {/* Items du projet */}
                          {projectData.items && projectData.items.length > 0 ? (
                            <div className="mt-6 space-y-4">
                              <h4 className="font-medium text-gray-900 border-b border-gray-200 pb-2">
                                Équipements du projet ({projectData.items.length})
                              </h4>

                              {/* Tableau simple défilant: Titre | Qté | Prix à neuf */}
                              <div className="max-h-96 overflow-y-auto border border-gray-200 rounded-lg">
                                <table className="w-full text-sm">
                                  <thead className="bg-gray-50 sticky top-0">
                                    <tr>
                                      <th className="text-left py-3 px-4 text-gray-700 font-medium">Titre</th>
                                      <th className="text-right py-3 px-4 text-gray-700 font-medium">Qté</th>
                                      <th className="text-right py-3 px-4 text-gray-700 font-medium">Prix à neuf (unitaire)</th>
                                    </tr>
                                  </thead>
                                  <tbody>
                                    {projectData.items.map((item, index) => (
                                      <tr key={index} className="border-b border-gray-100 hover:bg-gray-50">
                                        <td className="py-3 px-4">
                                          <span className="font-medium text-gray-900">
                                            {item.displayname || item.name || item.title || 'N/A'}
                                          </span>
                                        </td>
                                        <td className="py-3 px-4 text-right text-gray-700">
                                          {item.quantity || item.quantity_total || item.amount || item.count || 1}
                                        </td>
                                        <td className="py-3 px-4 text-right font-medium text-gray-900">
                                          {(() => {
                                            // Prix à neuf unitaire de l'équipement
                                            const unitNew = item.equipment_price_new ?? item.total_new_price; // fallback si API retourne unitaire dans total_new_price
                                            if (unitNew !== undefined && unitNew !== null && !isNaN(parseFloat(unitNew))) {
                                              return `${parseFloat(unitNew).toFixed(2)} €`;
                                            }
                                            return 'N/A';
                                          })()}
                                        </td>
                                      </tr>
                                    ))}
                                  </tbody>
                                </table>
                              </div>

                              {/* Total prix à neuf (si disponible) */}
                            </div>
                          ) : (
                            <div className="mt-6 p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
                              <div className="flex items-center">
                                <AlertCircle className="h-5 w-5 text-yellow-500 mr-2" />
                                <div>
                                  <p className="text-sm text-yellow-700 font-medium">
                                    Aucun équipement trouvé pour ce projet
                                  </p>
                                  <p className="text-xs text-yellow-600 mt-1">
                                    Les équipements de ce projet ne sont pas disponibles via l'API Rentman ou nécessitent des permissions supplémentaires.
                                  </p>
                                  <p className="text-xs text-yellow-600 mt-1">
                                    Vérifiez les logs du backend pour plus de détails sur la récupération des équipements.
                                  </p>
                                </div>
                              </div>
                            </div>
                          )}

                          {/* Actions */}
                          <div className="mt-6 flex justify-end space-x-3">
                            <button
                              onClick={() => {
                                setProjectData(null)
                                setProjectId('')
                                setProjectError('')
                                setSearchType('number')
                              }}
                              className="btn-outline"
                            >
                              Nouvelle recherche
                            </button>
                          </div>
                        </div>
                      )}
                    </div>
                  )}


                </div>
              )}

              {/* Import Clients */}
              {activeRentmanTab === 'import_clients' && (
                <div className="space-y-6">
                  <div className="border border-gray-200 rounded-lg p-6">
                    <h3 className="text-lg font-semibold text-gray-900 mb-2">
                      Options d'import des clients
                    </h3>
                    <p className="text-sm text-gray-600">
                      Configuration et synchronisation des clients depuis Rentman. (Bientôt disponible)
                    </p>
                    <div className="mt-4 space-y-4">
                      <div className="flex items-center justify-between">
                        <div>
                          <label className="text-sm font-medium text-gray-700">Activer la synchronisation manuelle des clients</label>
                          <p className="text-sm text-gray-500 mt-1">N'autoriser l'import des clients que via le bouton manuel ci-dessous</p>
                        </div>
                        <input
                          type="checkbox"
                          checked={Boolean(settings.import_clients_manual)}
                          onChange={(e) => {
                            const val = e.target.checked
                            const next = { ...settings, import_clients_manual: val }
                            setSettings(next)
                            saveMutation.mutate(next)
                          }}
                          className="h-4 w-4 text-orange-600 focus:ring-orange-500 border-gray-300 rounded"
                        />
                      </div>
                    </div>
                    <div className="mt-4 flex gap-3">
                      <button
                        onClick={async () => {
                          try {
                            const res = await apiService.get('/rentman/contacts/count');
                            const n = res.data?.count ?? 0
                            toast.success(`${n} contacts détectés sur Rentman`)
                          } catch (e) {
                            toast.error(e.response?.data?.message || 'Erreur comptage contacts')
                          }
                        }}
                        disabled={!isAuthenticated}
                        className={`btn-secondary ${(!isAuthenticated) ? 'opacity-50 cursor-not-allowed' : ''}`}
                      >
                        Compter les contacts
                      </button>
                      <button
                        onClick={async () => {
                          try {
                            const res = await apiService.post('/rentman/import-contacts');
                            toast.success(`Import clients terminé: ${res.data.processed} traités, ${res.data.inserted} insérés, ${res.data.updated} MAJ`)
                          } catch (e) {
                            toast.error(e.response?.data?.message || 'Erreur import clients')
                          }
                        }}
                        disabled={!isAuthenticated || !settings.import_clients_manual}
                        className={`btn-primary ${(!isAuthenticated || !settings.import_clients_manual) ? 'opacity-50 cursor-not-allowed' : ''}`}
                      >
                        Importer les clients
                      </button>
                      {(!isAuthenticated || !settings.import_clients_manual) && (
                        <span className="text-sm text-gray-500 self-center">
                          { !isAuthenticated ? 'Connexion admin requise' : 'Activez la synchronisation manuelle pour importer'}
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              )}

              {/* Users Management */}
              {activeTab === 'users' && (
                <div className="space-y-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <h2 className="text-2xl font-semibold text-gray-900">
                        Gestion des utilisateurs
                      </h2>
                      <nav className="flex items-center space-x-2 text-sm text-gray-500 mt-1">
                        <span>Maintenance</span>
                        <span>›</span>
                        <span className="text-orange-600">Utilisateurs</span>
                      </nav>
                    </div>
                    <div className="text-sm text-gray-500 bg-gray-50 px-3 py-1 rounded-full">
                      Gestion des comptes administrateurs
                    </div>
                  </div>

                  {/* Liste des utilisateurs */}
                  <div className="border border-gray-200 rounded-lg p-6">
                    <div className="flex items-center justify-between mb-4">
                      <h3 className="text-lg font-semibold text-gray-900">
                        Utilisateurs existants
                      </h3>
                      <button
                        onClick={() => {
                          setEditingUser(null)
                          setShowUserForm(true)
                        }}
                        className="btn-primary"
                      >
                        <Users className="h-4 w-4 mr-2" />
                        Ajouter un utilisateur
                      </button>
                    </div>

                    {/* Filtre par rôle */}
                    <div className="mb-4">
                      <div className="flex items-center space-x-4">
                        <label className="text-sm font-medium text-gray-700">
                          Filtrer par rôle :
                        </label>
                        <select
                          value={userRoleFilter}
                          onChange={(e) => setUserRoleFilter(e.target.value)}
                          className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500 text-sm"
                        >
                          <option value="all">Tous les utilisateurs</option>
                          <option value="admin">Administrateurs</option>
                          <option value="user">Utilisateurs</option>
                        </select>
                        <span className="text-sm text-gray-500">
                          ({users.filter(user => {
                            const matchesRole = userRoleFilter === 'all' || user.role === userRoleFilter;
                            const matchesSearch = !userSearchTerm || 
                              user.username.toLowerCase().includes(userSearchTerm.toLowerCase()) ||
                              user.email.toLowerCase().includes(userSearchTerm.toLowerCase()) ||
                              (user.first_name && user.first_name.toLowerCase().includes(userSearchTerm.toLowerCase())) ||
                              (user.last_name && user.last_name.toLowerCase().includes(userSearchTerm.toLowerCase()));
                            return matchesRole && matchesSearch;
                          }).length} utilisateur{users.filter(user => {
                            const matchesRole = userRoleFilter === 'all' || user.role === userRoleFilter;
                            const matchesSearch = !userSearchTerm || 
                              user.username.toLowerCase().includes(userSearchTerm.toLowerCase()) ||
                              user.email.toLowerCase().includes(userSearchTerm.toLowerCase()) ||
                              (user.first_name && user.first_name.toLowerCase().includes(userSearchTerm.toLowerCase())) ||
                              (user.last_name && user.last_name.toLowerCase().includes(userSearchTerm.toLowerCase()));
                            return matchesRole && matchesSearch;
                          }).length > 1 ? 's' : ''})
                        </span>
                      </div>
                    </div>

                    {isLoadingUsers ? (
                      <div className="text-center py-8">
                        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-orange-500 mx-auto mb-4"></div>
                        <p className="text-gray-600">Chargement des utilisateurs...</p>
                      </div>
                    ) : (
                      <div className="overflow-x-auto">
                        <table className="min-w-full divide-y divide-gray-200">
                          <thead className="bg-gray-50">
                            <tr>
                              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                Nom d'utilisateur
                              </th>
                              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                Email
                              </th>
                              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                Rôle
                              </th>
                              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                Date de création
                              </th>
                              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                Actions
                              </th>
                            </tr>
                          </thead>
                          <tbody className="bg-white divide-y divide-gray-200">
                            {users
                              .filter(user => {
                                const matchesRole = userRoleFilter === 'all' || user.role === userRoleFilter;
                                const matchesSearch = !userSearchTerm || 
                                  (user.username && user.username.toLowerCase().includes(userSearchTerm.toLowerCase())) ||
                                  (user.email && user.email.toLowerCase().includes(userSearchTerm.toLowerCase())) ||
                                  (user.first_name && user.first_name.toLowerCase().includes(userSearchTerm.toLowerCase())) ||
                                  (user.last_name && user.last_name.toLowerCase().includes(userSearchTerm.toLowerCase()));
                                return matchesRole && matchesSearch;
                              })
                              .map((user) => (
                              <tr key={user.id}>
                                <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                                  {user.username}
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                  {user.email}
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap">
                                  <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                                    user.role === 'admin' 
                                      ? 'bg-orange-100 text-orange-800' 
                                      : 'bg-gray-100 text-gray-800'
                                  }`}>
                                    {user.role === 'admin' ? 'Administrateur' : 'Utilisateur'}
                                  </span>
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                  {new Date(user.created_at).toLocaleDateString('fr-FR')}
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                                  <div className="flex space-x-2">
                                    <button
                                      onClick={() => {
                                        setEditingUser(user)
                                        setShowUserForm(true)
                                      }}
                                      className="text-orange-600 hover:text-orange-900"
                                    >
                                      <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                                      </svg>
                                    </button>
                                    <button
                                      onClick={() => handleDeleteUser(user.id)}
                                      className="text-red-600 hover:text-red-900"
                                    >
                                      <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                      </svg>
                                    </button>
                                  </div>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </div>

                  {/* Formulaire utilisateur */}
                  {showUserForm && (
                    <div className="border border-gray-200 rounded-lg p-6">
                      <h3 className="text-lg font-semibold text-gray-900 mb-4">
                        {editingUser ? 'Modifier l\'utilisateur' : 'Nouvel utilisateur'}
                      </h3>
                      
                      <form onSubmit={(e) => {
                        e.preventDefault()
                        const formData = new FormData(e.target)
                        const userData = {
                          username: formData.get('username'),
                          email: formData.get('email'),
                          first_name: formData.get('first_name'),
                          last_name: formData.get('last_name'),
                          role: formData.get('role'),
                          password: formData.get('password')
                        }
                        handleSaveUser(userData)
                      }}>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                              Nom d'utilisateur *
                            </label>
                            <input
                              type="text"
                              name="username"
                              defaultValue={editingUser?.username || ''}
                              required
                              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500"
                            />
                          </div>
                          
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                              Email *
                            </label>
                            <input
                              type="email"
                              name="email"
                              defaultValue={editingUser?.email || ''}
                              required
                              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500"
                            />
                          </div>
                          
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                              Prénom *
                            </label>
                            <input
                              type="text"
                              name="first_name"
                              defaultValue={editingUser?.first_name || ''}
                              required
                              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500"
                            />
                          </div>
                          
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                              Nom *
                            </label>
                            <input
                              type="text"
                              name="last_name"
                              defaultValue={editingUser?.last_name || ''}
                              required
                              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500"
                            />
                          </div>
                          
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                              Rôle *
                            </label>
                            <select
                              name="role"
                              defaultValue={editingUser?.role || 'user'}
                              required
                              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500"
                            >
                              <option value="user">Utilisateur</option>
                              <option value="admin">Administrateur</option>
                            </select>
                          </div>
                          
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                              Mot de passe {!editingUser && '*'}
                            </label>
                            <input
                              type="password"
                              name="password"
                              required={!editingUser}
                              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500"
                            />
                            {editingUser && (
                              <p className="text-xs text-gray-500 mt-1">
                                Laissez vide pour conserver le mot de passe actuel
                              </p>
                            )}
                          </div>
                        </div>
                        
                        <div className="flex justify-end space-x-3 mt-6">
                          <button
                            type="button"
                            onClick={() => {
                              setShowUserForm(false)
                              setEditingUser(null)
                            }}
                            className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 border border-gray-300 rounded-md hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-gray-500"
                          >
                            Annuler
                          </button>
                          <button
                            type="submit"
                            disabled={isSavingUser}
                            className="btn-primary disabled:opacity-50"
                          >
                            {isSavingUser ? 'Sauvegarde...' : (editingUser ? 'Modifier' : 'Créer')}
                          </button>
                        </div>
                      </form>
                    </div>
                  )}
                </div>
              )}

            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

// Composant formulaire de projet

export default AdminSettings
